import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiRefreshCw,
  FiPaperclip,
} from "react-icons/fi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import activeLoadStatusIcon from "../../../assets/images/Active_load.svg";
import inactiveStatusIcon from "../../../assets/images/Inactive_icon.svg";
import statusDecommissionedIcon from "../../../assets/images/Status Decommissioned.svg";
import statusUnderMaintenanceIcon from "../../../assets/images/Status Under Maintenance.svg";
import AssetLinkField from "../../../components/AssetLinkField";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomModal from "../../../components/CustomModal";
import FileUpload from "../../../components/FileUpload";
import CustomTabs from "../../../components/CustomTabs";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import NewTableComponent from "../../../components/NewTableComponent";
import Select from "../../../components/Select";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Trip.css";
import { apiService } from "../../../services/apiService";
import {
  attachEquipmentTyres,
  clearEquipmentAssignmentHistoryState,
  clearEquipmentDropdowns,
  clearEquipmentHistoryState,
  clearEquipmentDetailState,
  fetchEquipmentDropdown,
  commentOnEquipment,
  createEquipmentInstallment,
  fetchEquipmentDetail,
  fetchEquipmentAssignmentHistory,
  fetchEquipmentDistanceSummary,
  fetchEquipmentHistory,
  fetchEquipmentInstallments,
  fetchEquipmentManufacturersDropdown,
  fetchEquipmentModelsDropdown,
  fetchEquipmentServiceRecords,
  fetchEquipmentTrips,
  fetchEquipmentTypesDropdown,
  fetchEquipmentVendorsDropdown,
  updateEquipmentGeneralInfo,
  updateEquipmentPurchaseInfo,
} from "../../../store/Slices/EquipmentSlice";
import {
  attachVehicleEquipment,
  detachVehicleTyre,
  detachVehicleEquipment,
} from "../../../store/Slices/ServiceSlice";
import { fetchBanksDropdown } from "../../../store/Slices/dropdownSlice";
import { fetchTyresDropdown } from "../../../store/Slices/TyreSlice";
import {
  AppMainModules,
  AssetsPermission,
  AssetsSubModules,
} from "../../../utils/app-constant";
import {
  findCollectionByKeys,
  formats,
  getCurrentUser,
  hasPermission,
  modules,
  normalizeOptions,
} from "../../../utils/helper";
import {
  checkedIcon,
  closedIcon,
  inProgressIcon,
  linkedIcon,
  openIcon,
  pendingIcon,
} from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import {
  brakeSystemOptions,
  containerSizeOptions,
  coverageTypeOptions,
  depreciationMethodOptions,
  equipmentStatusOptions,
  insuranceProviderOptions,
  paymentMethodOptions,
  paymentTypeOptions,
  suspensionTypeOptions,
} from "./equipmentAssets.constants";

const normalizeAssetsRegistrationTab = (value, fallback = "equipments") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "equipments") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "equipments") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isFullPaymentType = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_") === "full_payment";

const getPositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

const formatAmount = (value) => normalizeNumber(value, 0).toLocaleString("en-US");

const normalizeUploadedFiles = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map((file) => file?.fileName || file?.file_path || file?.path || file)
    .filter(Boolean);

const normalizeInstallmentFiles = (files = []) => normalizeUploadedFiles(files);

const getInstallmentAttachmentPath = (file) => {
  if (!file) return "";
  if (typeof file === "string") return file;
  return (
    file?.file_path ||
    file?.fileName ||
    file?.file_name ||
    file?.path ||
    file?.url ||
    ""
  );
};

const buildInstallmentAttachmentUrl = (filePath = "") => {
  const value = String(filePath || "").trim();
  if (!value) return "";
  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  return `${process.env.REACT_APP_API_FILES_URL}${value}`;
};

const getInstallmentAttachmentName = (url = "") => {
  const cleanUrl = String(url || "").split("?")[0];
  const name = cleanUrl.split("/").pop() || "Attachment";
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const isImageAttachment = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(url).split("?")[0]);

const resolveInstallmentAttachmentUrls = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map(getInstallmentAttachmentPath)
    .filter(Boolean)
    .map(buildInstallmentAttachmentUrl)
    .filter(Boolean);

const toInstallmentDateTimeString = (dateValue) => {
  const normalized = String(dateValue || "").trim();
  if (!normalized) return "";
  return `${normalized} 00:00:00`;
};

const createInstallmentFormState = () => ({
  paymentMethod: "",
  amount: "",
  submissionDate: "",
  notes: "",
  files: [],
  bankName: "",
  accountTitle: "",
  ibanNumber: "",
  referenceNumber: "",
});

const normalizeEquipmentStatusValue = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : "";
  }

  return String(value).trim();
};

const normalizeComparableFiles = (files = []) =>
  normalizeUploadedFiles(files)
    .map((file) => String(file || "").trim())
    .filter(Boolean)
    .sort();

const areComparableArraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const buildEquipmentGeneralInfoSnapshot = ({
  equipmentId,
  values,
  attachments = [],
  equipmentTypeId,
  manufacturerId,
  modelId,
  supplierVendorId,
}) => ({
  equipment_id: getPositiveNumber(equipmentId),
  asset_number: normalizeComparableValue(values?.assetNumber),
  equipment_type_id: getPositiveNumber(equipmentTypeId),
  serial_number: normalizeComparableValue(values?.serialNumber),
  status: normalizeEquipmentStatusValue(values?.status),
  description: String(values?.description || "").trim(),
  manufacturer_id: getPositiveNumber(manufacturerId),
  model_id: getPositiveNumber(modelId),
  year_of_manufacture: normalizeNumber(values?.yearOfManufacture, 0),
  supplier_vendor_id: getPositiveNumber(supplierVendorId),
  number_of_axles: normalizeNumber(values?.numberOfAxles, 0),
  number_of_tyres: normalizeNumber(values?.numberOfTyres, 0),
  brake_system: normalizeComparableValue(values?.brakeSystem),
  suspension_type: normalizeComparableValue(values?.suspensionType),
  capacity: normalizeNumber(values?.capacityValue, 0),
  number_of_compartments: normalizeNumber(values?.numberOfCompartments, 0),
  container_size: normalizeComparableValue(values?.containerSize),
  load_capacity: normalizeNumber(values?.loadCapacity, 0),
  length: normalizeNumber(values?.length, 0),
  width: normalizeNumber(values?.width, 0),
  height: normalizeNumber(values?.height, 0),
  tare_weight: normalizeNumber(values?.tareWeight, 0),
  gross_weight: normalizeNumber(values?.grossWeight, 0),
  files: normalizeUploadedFiles(attachments),
});

const getUpdatedEquipmentGeneralInfoKeys = (currentSnapshot = {}, initialSnapshot = {}) =>
  Object.keys(currentSnapshot).filter((key) => {
    if (key === "equipment_id") return false;

    if (key === "files") {
      return !areComparableArraysEqual(
        normalizeComparableFiles(currentSnapshot[key]),
        normalizeComparableFiles(initialSnapshot[key])
      );
    }

    return (
      normalizeComparableValue(currentSnapshot[key]) !==
      normalizeComparableValue(initialSnapshot[key])
    );
  });

const buildEquipmentPurchaseInfoSnapshot = (equipmentId, values = {}) => ({
  equipment_id: getPositiveNumber(equipmentId),
  purchase_at: normalizeComparableValue(values.purchaseDate),
  purchase_cost: normalizeNumber(values.purchaseCost, 0),
  payment_type: normalizeComparableValue(values.paymentType),
  depreciation_method: normalizeComparableValue(values.depreciationMethod),
  useful_life: normalizeNumber(values.usefulLife, 0),
  residual_value: normalizeNumber(values.residualValue, 0),
  asset_insurances: {
    provider: normalizeComparableValue(values.insuranceProvider),
    policy_number: normalizeComparableValue(values.policyNumber),
    coverage_type: normalizeComparableValue(values.coverageType),
    coverage_amount: normalizeNumber(values.coverageAmount, 0),
    premium_amount: normalizeNumber(values.premiumAmount, 0),
    start_date: normalizeComparableValue(values.insuranceStartDate),
    expiry_date: normalizeComparableValue(values.insuranceExpiryDate),
  },
});

const getUpdatedEquipmentPurchaseInfoKeys = (currentSnapshot = {}, initialSnapshot = {}) => {
  const updatedKeys = [];

  Object.entries(currentSnapshot).forEach(([key, value]) => {
    if (key === "equipment_id") return;

    if (key === "asset_insurances") {
      Object.entries(value || {}).forEach(([insuranceKey, insuranceValue]) => {
        if (
          normalizeComparableValue(insuranceValue) !==
          normalizeComparableValue(initialSnapshot?.asset_insurances?.[insuranceKey])
        ) {
          updatedKeys.push(`asset_insurances.${insuranceKey}`);
        }
      });
      return;
    }

    if (
      normalizeComparableValue(value) !==
      normalizeComparableValue(initialSnapshot[key])
    ) {
      updatedKeys.push(key);
    }
  });

  return updatedKeys;
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const pickFirstMeaningfulValue = (...values) => {
  for (const value of values) {
    if (hasMeaningfulValue(value)) {
      return value;
    }
  }

  return "";
};

const mergeNonEmptyValues = (baseValues = {}, nextValues = {}) => {
  const merged = { ...(baseValues || {}) };

  Object.entries(nextValues || {}).forEach(([key, value]) => {
    if (hasMeaningfulValue(value)) {
      merged[key] = value;
    }
  });

  return merged;
};

const getUserImage = (user) =>
  user?.image ||
  user?.profile_image ||
  user?.profileImage ||
  user?.avatar ||
  user?.details?.image ||
  user?.details?.avatar ||
  "";

const statusMeta = (status) => {
  const normalized = normalizeEquipmentStatusValue(status);
  if (normalized === "active") {
    return { label: "Active", color: "#299900", icon: activeLoadStatusIcon };
  }
  if (normalized === "under_maintenance") {
    return {
      label: "Under Maintenance",
      color: "#f2841b",
      icon: statusUnderMaintenanceIcon,
    };
  }
  if (normalized === "decommissioned") {
    return { label: "Decommissioned", color: "#0a53e8", icon: statusDecommissionedIcon };
  }
  return { label: "Inactive", color: "#ef0000", icon: inactiveStatusIcon };
};

const serviceStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "open") return { label: "Open", icon: openIcon, color: "#999999" };
  if (normalized === "pending") return { label: "Pending", icon: pendingIcon, color: "#ff7f02" };
  if (normalized === "closed") return { label: "Closed", icon: closedIcon, color: "#ef0000" };
  if (normalized === "in_progress") {
    return { label: "In progress", icon: inProgressIcon, color: "#4014b8" };
  }
  return { label: "Completed", icon: checkedIcon, color: "#299900" };
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const findOption = (options = [], value = "") =>
  findOptionByOptionIdOrValue(options, getPositiveNumber(value), value);

const ensureCurrentOption = (options = [], value = "") => {
  const normalizedValue = normalizeLinkedValue(value);
  if (!normalizedValue) return options;
  if (findOption(options, normalizedValue)) return options;

  return [{ label: normalizedValue, value: normalizedValue }, ...options];
};

const findOptionByOptionIdOrValue = (options = [], optionId = "", value = "") => {
  const normalizedOptionId = String(optionId ?? "").trim();
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  if (normalizedOptionId) {
    const matchedById = options.find(
      (option) => String(option?.optionId ?? option?.id ?? "").trim() === normalizedOptionId
    );
    if (matchedById) return matchedById;
  }

  return (
    (Array.isArray(options) ? options : []).find((option) => {
      const optionValue = String(option?.value ?? "").trim().toLowerCase();
      const optionLabel = String(option?.label ?? "").trim().toLowerCase();
      return optionValue === normalizedValue || optionLabel === normalizedValue;
    }) || null
  );
};

const toLabelValueOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((option) => ({
    ...option,
    optionId: option?.value ?? option?.id ?? option?.optionId,
    label: String(option?.label || option?.name || option?.title || option?.value || "").trim(),
    value: String(option?.label || option?.name || option?.title || option?.value || "").trim(),
  }));

const resolveSelectOptionId = (
  options = [],
  value = "",
  { fallbackId = null, initialValue = "" } = {}
) => {
  const directValueId = getPositiveNumber(value);
  if (directValueId) {
    return directValueId;
  }

  const matchedOption = findOption(options, value);
  const matchedId = getPositiveNumber(
    matchedOption?.optionId,
    matchedOption?.id,
    matchedOption?.value
  );

  if (matchedId) {
    return matchedId;
  }

  const normalizedValue = String(value || "").trim().toLowerCase();
  const normalizedInitialValue = String(initialValue || "").trim().toLowerCase();
  if (normalizedValue && normalizedValue === normalizedInitialValue) {
    return getPositiveNumber(fallbackId);
  }

  return null;
};

const resolveEquipmentDetailId = (routeValue, stateEquipment) => {
  const stateId = Number(stateEquipment?.id);
  if (Number.isFinite(stateId) && stateId > 0) return stateId;

  const directRouteId = Number(routeValue);
  if (Number.isFinite(directRouteId) && directRouteId > 0) return directRouteId;

  const digitsMatch = String(routeValue || "").match(/(\d+)$/);
  if (!digitsMatch) return null;

  const derivedId = Number(digitsMatch[1]);
  return Number.isFinite(derivedId) && derivedId > 0 ? derivedId : null;
};

const findLinkedVehicle = (vehicles = [], linkedVehicleId = "") => {
  const normalizedLinkedId = String(linkedVehicleId || "").trim().toLowerCase();
  if (!normalizedLinkedId) return null;

  return (
    vehicles.find(
      (row) => String(row?.registrationNumber || "").trim().toLowerCase() === normalizedLinkedId
    ) ||
    vehicles.find((row) => String(row?.systemId || "").trim().toLowerCase() === normalizedLinkedId) ||
    null
  );
};

const getLinkedVehicleEquipmentId = (vehicleOption) =>
  getPositiveNumber(
    vehicleOption?.equipment_id,
    vehicleOption?.linked_equipment_id,
    vehicleOption?.current_equipment_id,
    vehicleOption?.installed_equipment_id,
    vehicleOption?.current_installed_equipment_id,
    vehicleOption?.equipment?.id,
    vehicleOption?.equipment?.equipment_id,
    vehicleOption?.current_equipment?.id,
    vehicleOption?.currentEquipment?.id
  );

const buildVehicleHistoryData = (formData = {}, linkedVehicle = null) => {
  const source = formData?.vehicleHistory;
  if (Array.isArray(source)) return source;

  return [
    {
      equipmentId: linkedVehicle?.systemId || "VHC-001",
      startDate: formData?.assignmentStartDate || "2024-03-01",
      endDate: formData?.assignmentEndDate || "2026-02-28",
      duration: formData?.assignmentDuration || "1 year, 11 months, 27 days",
    },
  ];
};

const buildTyreHistoryData = ({
  formData = {},
  allTyres = [],
  equipmentId = "",
  linkedVehicleId = "",
  linkedVehicleRegistration = "",
}) => {
  const source = formData?.tyreHistory;
  if (Array.isArray(source)) return source;

  const normalizedEquipmentId = String(equipmentId || "").trim().toLowerCase();
  const normalizedVehicleIds = [linkedVehicleId, linkedVehicleRegistration]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  const preferredTyres = allTyres
    .filter((row) => {
      const tyreEquipmentId = String(row?.linkedEquipmentId || "").trim().toLowerCase();
      const tyreVehicleId = String(row?.linkedVehicleId || "").trim().toLowerCase();
      return tyreEquipmentId === normalizedEquipmentId || normalizedVehicleIds.includes(tyreVehicleId);
    })
    .slice(0, 4)
    .map((row) => ({
      tyreId: row?.systemId || "-",
      position: row?.currentPosition || "-",
      installedDate: row?.lastInstalledDate || row?.purchaseDate || "-",
      removedDate: row?.removedDate || "-",
      startOdometer: row?.startOdometer || row?.totalKmRun || "-",
      endOdometer: row?.endOdometer || row?.totalKmRun || "-",
      kmDriven: row?.totalKmRun || "-",
    }));

  if (preferredTyres.length) return preferredTyres;

  return [
    {
      tyreId: "TYR-001",
      position: "Axle X - Left",
      installedDate: "2026-01-15",
      removedDate: "-",
      startOdometer: "12,450",
      endOdometer: "12,450",
      kmDriven: "0",
    },
    {
      tyreId: "TYR-002",
      position: "Axle X - Right",
      installedDate: "2026-01-28",
      removedDate: "-",
      startOdometer: "18,320",
      endOdometer: "18,320",
      kmDriven: "0",
    },
    {
      tyreId: "TYR-003",
      position: "Axle X - Left",
      installedDate: "2026-02-10",
      removedDate: "-",
      startOdometer: "25,775",
      endOdometer: "25,775",
      kmDriven: "0",
    },
    {
      tyreId: "TYR-004",
      position: "Axle X - Right",
      installedDate: "2026-02-18",
      removedDate: "-",
      startOdometer: "31,090",
      endOdometer: "31,090",
      kmDriven: "0",
    },
  ];
};

const buildInstallmentScheduleData = (formData = {}) => {
  const source = formData?.installmentSchedule;
  if (Array.isArray(source)) return source;

  return [
    { number: "01", dueDate: "2025-05-15", amount: 120500, status: "paid" },
    { number: "02", dueDate: "2025-06-15", amount: 120500, status: "overdue" },
    { number: "04", dueDate: "2025-07-15", amount: 120500, status: "pending" },
  ];
};

const baseServiceRows = [
  {
    id: "SE-001",
    workOrderId: "WO-12345",
    serviceType: "Scheduled",
    serviceDate: "2026-01-15",
    odometer: "12,450",
    partCost: 129000,
    labourCost: 129000,
    totalCost: 129000,
    status: "open",
  },
  {
    id: "SE-002",
    workOrderId: "WO-12345",
    serviceType: "Unscheduled",
    serviceDate: "2026-01-28",
    odometer: "18,320",
    partCost: 73600,
    labourCost: 73600,
    totalCost: 73600,
    status: "pending",
  },
  {
    id: "SE-003",
    workOrderId: "WO-12345",
    serviceType: "Scheduled",
    serviceDate: "2026-02-10",
    odometer: "25,775",
    partCost: 107000,
    labourCost: 107000,
    totalCost: 107000,
    status: "closed",
  },
  {
    id: "SE-004",
    workOrderId: "WO-12345",
    serviceType: "Unscheduled",
    serviceDate: "2026-02-18",
    odometer: "31,090",
    partCost: 150000,
    labourCost: 150000,
    totalCost: 150000,
    status: "in_progress",
  },
  {
    id: "SE-005",
    workOrderId: "WO-12345",
    serviceType: "Unscheduled",
    serviceDate: "2026-02-18",
    odometer: "31,090",
    partCost: 107000,
    labourCost: 107000,
    totalCost: 73600,
    status: "completed",
  },
];

const defaultEquipmentActivities = [
  {
    id: "equipment-log-1",
    type: "activity",
    text: 'Trip status changed: "Active" to "Completed"',
    time: "12:10 PM",
    createdAtTs: 2,
  },
  {
    id: "equipment-log-2",
    type: "activity",
    text: "Violation Alert: Exceeded speed limit (120 km/h)",
    time: "11:15 AM",
    createdAtTs: 1,
  },
];

const countryOptions = [
  { label: "United States", value: "United States" },
  { label: "Pakistan", value: "Pakistan" },
  { label: "China", value: "China" },
];

const tyreConfigurationOptions = [
  { label: "Single Tyres", value: "single" },
  { label: "Dual Tyres", value: "dual" },
];

const swapTypeOptions = [
  { label: "Equipment", value: "equipment" },
  { label: "Vehicle", value: "vehicle" },
];

const defaultEquipmentAxleAssignments = {
  left: "",
  right: "",
  leftInner: "",
  leftOuter: "",
  rightInner: "",
  rightOuter: "",
};

const equipmentAxleAssignmentFields = {
  single: [
    { key: "left", label: (axleId) => `${axleId} Left` },
    { key: "right", label: (axleId) => `${axleId} Right` },
  ],
  dual: [
    { key: "leftOuter", label: (axleId) => `${axleId} Left Outer` },
    { key: "leftInner", label: (axleId) => `${axleId} Left Inner` },
    { key: "rightOuter", label: (axleId) => `${axleId} Right Outer` },
    { key: "rightInner", label: (axleId) => `${axleId} Right Inner` },
  ],
};

const normalizeLinkedValue = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const lower = normalized.toLowerCase();
  if (lower === "0" || lower === "n/a" || lower === "null") return "";

  return normalized;
};

const normalizeSwapAssetType = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "vehicle" ? "vehicle" : "equipment";
};

const getSwapAssetFieldLabel = (assetType = "") => {
  if (assetType === "vehicle") return "Vehicle ID";
  if (assetType === "equipment") return "Equipment ID";
  return "Asset ID";
};

const mapAssetTyreOptions = (assetTyres = []) =>
  (Array.isArray(assetTyres) ? assetTyres : []).map((item) => {
    const fallbackValue = [
      item?.axle_id,
      item?.position_identifier,
      item?.tyre_id || item?.tyre?.id,
    ]
      .filter(Boolean)
      .join(":");
    const tyreLabel =
      String(item?.tyre?.tyres_id || "").trim() ||
      (item?.tyre_id || item?.tyre?.id ? `TY-${item?.tyre_id || item?.tyre?.id}` : "Unassigned");

    return {
      label: `${item?.position || "Position"} - ${tyreLabel}`,
      value: String(item?.id || fallbackValue),
      optionId: getPositiveNumber(item?.id),
      assignmentId: getPositiveNumber(item?.id),
      axleId: getPositiveNumber(item?.axle_id),
      tyreId: getPositiveNumber(item?.tyre_id, item?.tyre?.id),
      position: String(item?.position || "").trim(),
      positionIdentifier: String(item?.position_identifier || "").trim(),
      tyreSystemId: String(item?.tyre?.tyres_id || "").trim(),
    };
  });

const createEquipmentAxleConfiguration = (index, savedConfiguration = {}) => ({
  id: savedConfiguration?.id || "",
  sourceId: savedConfiguration?.sourceId || savedConfiguration?.source_id || "",
  sourceType: savedConfiguration?.sourceType || savedConfiguration?.source_type || "",
  axleId: savedConfiguration?.axleId || `A${index + 1}`,
  tyreConfiguration: savedConfiguration?.tyreConfiguration || "single",
  assignments: {
    ...defaultEquipmentAxleAssignments,
    ...(savedConfiguration?.assignments || {}),
  },
});

const buildEquipmentAxleConfigurations = (savedConfigurations = [], axleCount = 3) =>
  Array.from({ length: Math.max(1, axleCount) }, (_, index) =>
    createEquipmentAxleConfiguration(index, savedConfigurations[index])
  );

const getEquipmentAxleAssignmentFields = (tyreConfiguration) =>
  equipmentAxleAssignmentFields[tyreConfiguration] || equipmentAxleAssignmentFields.single;

const buildEquipmentTyreLinkSelections = (axleConfigurations = [], tyreOptions = []) =>
  (Array.isArray(axleConfigurations) ? axleConfigurations : []).map((axle, index) => {
    const axleId = String(axle?.axleId || `A${index + 1}`);
    const assignmentFields = getEquipmentAxleAssignmentFields(axle?.tyreConfiguration);

    return {
      axleId,
      axleRecordId: getPositiveNumber(axle?.id),
      selections: assignmentFields.map((field) => {
        const value = axle?.assignments?.[field.key] || "";

        return {
          fieldKey: field.key,
          value,
          resolvedTyreId: resolveSelectOptionId(tyreOptions, value, {
            fallbackId: getPositiveNumber(value),
            initialValue: value,
          }),
        };
      }),
    };
  });

const linkedEquipmentTyresSchema = Yup.array()
  .of(
    Yup.object().shape({
      axleId: Yup.string().required(),
      axleRecordId: Yup.number().typeError("Axle is required").required("Axle is required"),
      selections: Yup.array()
        .of(
          Yup.object().shape({
            fieldKey: Yup.string().required(),
            resolvedTyreId: Yup.number()
              .typeError("Tyre is required")
              .required("Tyre is required"),
          })
        )
        .min(1, "Tyre positions are required")
        .test(
          "all-positions-selected",
          "Please select tyres for all axle positions",
          (selections = []) =>
            selections.every((selection) => Boolean(getPositiveNumber(selection?.resolvedTyreId)))
        ),
    })
  )
  .min(1, "Equipment axle data is missing");

const createSwapTyreFormState = () => ({
  sourceType: "equipment",
  sourceAssetId: "",
  sourcePosition: "",
  targetType: "",
  targetAssetId: "",
  targetPosition: "",
});

const tabOptions = [
  { key: "general_info", label: "General Information" },
  { key: "service_records", label: "Service Records" },
  { key: "assignment_history", label: "Assignment History" },
  { key: "financial_insurance", label: "Financial & Insurance" },
  { key: "operational_data", label: "Operational Data" },
];

const getInitialValues = (equipment) => {
  const formData = equipment?.formData || {};
  const purchaseRecord = formData?.purchaseRecord || {};
  const installmentRecord = formData?.installmentRecord || {};
  const insuranceRecord = formData?.insuranceRecord || {};
  const savedAxleConfigurations = Array.isArray(formData?.axleConfigurations)
    ? formData.axleConfigurations
    : [];
  const initialAxleCount = normalizeNumber(
    pickFirstMeaningfulValue(formData?.numberOfAxles, equipment?.numberOfAxles),
    3
  );

  return {
    systemId: pickFirstMeaningfulValue(equipment?.systemId),
    assetNumber: pickFirstMeaningfulValue(
      formData?.assetNumber,
      equipment?.registrationNumber
    ),
    equipmentType: pickFirstMeaningfulValue(
      formData?.equipmentType,
      equipment?.equipmentType
    ),
    equipmentTypeId: pickFirstMeaningfulValue(formData?.equipmentTypeId),
    serialNumber: pickFirstMeaningfulValue(
      formData?.serialNumber,
      equipment?.serialNumber
    ),
    registrationNumber: pickFirstMeaningfulValue(
      formData?.registrationNumber,
      equipment?.registrationNumber
    ),
    status: normalizeEquipmentStatusValue(
      pickFirstMeaningfulValue(formData?.status, equipment?.status, "active")
    ),
    description: pickFirstMeaningfulValue(formData?.description),

    manufacturer: pickFirstMeaningfulValue(formData?.manufacturer),
    manufacturerId: pickFirstMeaningfulValue(formData?.manufacturerId),
    model: pickFirstMeaningfulValue(formData?.model),
    modelId: pickFirstMeaningfulValue(formData?.modelId),
    yearOfManufacture: pickFirstMeaningfulValue(formData?.yearOfManufacture),
    countryOfOrigin: pickFirstMeaningfulValue(
      formData?.countryOfOrigin,
      formData?.country_of_origin,
      equipment?.countryOfOrigin,
      equipment?.country_of_origin
    ),
    supplierVendor: pickFirstMeaningfulValue(
      formData?.supplierVendor,
      formData?.vendor
    ),
    supplierVendorId: pickFirstMeaningfulValue(
      formData?.supplierVendorId,
      formData?.vendorId
    ),

    numberOfAxles: pickFirstMeaningfulValue(
      formData?.numberOfAxles,
      equipment?.numberOfAxles
    ),
    axleConfigurations: buildEquipmentAxleConfigurations(
      savedAxleConfigurations,
      initialAxleCount
    ),
    configuration: pickFirstMeaningfulValue(formData?.configuration),
    numberOfTyres: pickFirstMeaningfulValue(
      formData?.numberOfTyres,
      equipment?.linkedTyres
    ),
    suspensionType: pickFirstMeaningfulValue(formData?.suspensionType),
    brakeSystem: pickFirstMeaningfulValue(formData?.brakeSystem),

    capacityValue: pickFirstMeaningfulValue(
      formData?.tankerBowserCapacity,
      formData?.capacityValue,
      formData?.capacity,
      equipment?.capacityValue,
      equipment?.capacity
    ),
    capacityUnit: pickFirstMeaningfulValue(
      formData?.capacityUnit,
      formData?.capacity_unit,
      equipment?.capacityUnit,
      equipment?.capacity_unit
    ),
    numberOfCompartments: pickFirstMeaningfulValue(formData?.numberOfCompartments),
    containerSize: pickFirstMeaningfulValue(formData?.containerSize),
    loadCapacity: pickFirstMeaningfulValue(formData?.loadCapacity),

    length: pickFirstMeaningfulValue(formData?.length),
    width: pickFirstMeaningfulValue(formData?.width),
    height: pickFirstMeaningfulValue(formData?.height),
    tareWeight: pickFirstMeaningfulValue(formData?.tareWeight),
    grossWeight: pickFirstMeaningfulValue(formData?.grossWeight),

    purchaseDate: pickFirstMeaningfulValue(
      formData?.purchaseDate,
      formData?.purchase_at,
      purchaseRecord?.purchase_at,
      purchaseRecord?.purchaseDate
    ),
    purchaseCost: pickFirstMeaningfulValue(
      formData?.purchaseCost,
      formData?.purchase_cost,
      purchaseRecord?.purchase_cost,
      purchaseRecord?.purchaseCost,
      equipment?.totalCost
    ),
    paymentType: pickFirstMeaningfulValue(
      formData?.paymentType,
      formData?.payment_type,
      purchaseRecord?.payment_type,
      purchaseRecord?.paymentType
    ),
    paymentMethod: pickFirstMeaningfulValue(
      formData?.paymentMethod,
      formData?.payment_method,
      purchaseRecord?.payment_method,
      purchaseRecord?.paymentMethod
    ),
    depreciationMethod: pickFirstMeaningfulValue(
      formData?.depreciationMethod,
      formData?.depreciation_method,
      purchaseRecord?.depreciation_method,
      purchaseRecord?.depreciationMethod
    ),
    usefulLife: pickFirstMeaningfulValue(
      formData?.usefulLife,
      formData?.useful_life,
      purchaseRecord?.useful_life,
      purchaseRecord?.usefulLife
    ),
    residualValue: pickFirstMeaningfulValue(
      formData?.residualValue,
      formData?.residual_value,
      purchaseRecord?.residual_value,
      purchaseRecord?.residualValue
    ),

    totalInstallments: pickFirstMeaningfulValue(
      formData?.totalInstallments,
      formData?.total_installments,
      installmentRecord?.total_installments,
      installmentRecord?.totalInstallments
    ),
    installmentAmount: pickFirstMeaningfulValue(
      formData?.installmentAmount,
      formData?.installment_amount,
      installmentRecord?.installment_amount,
      installmentRecord?.installmentAmount
    ),
    installmentFrequency: pickFirstMeaningfulValue(
      formData?.installmentFrequency,
      formData?.installment_frequency,
      installmentRecord?.installment_frequency,
      installmentRecord?.installmentFrequency
    ),
    firstPaymentDate: pickFirstMeaningfulValue(
      formData?.firstPaymentDate,
      formData?.first_payment_date,
      installmentRecord?.first_payment_date,
      installmentRecord?.firstPaymentDate
    ),
    interestRate: pickFirstMeaningfulValue(
      formData?.interestRate,
      formData?.interest_rate,
      installmentRecord?.interest_rate,
      installmentRecord?.interestRate
    ),
    financingInstitution: pickFirstMeaningfulValue(
      formData?.financingInstitution,
      formData?.financing_institution,
      installmentRecord?.financing_institution,
      installmentRecord?.financingInstitution
    ),

    insuranceProvider: pickFirstMeaningfulValue(
      formData?.insuranceProvider,
      formData?.insurance_provider,
      insuranceRecord?.provider,
      insuranceRecord?.insurance_provider
    ),
    policyNumber: pickFirstMeaningfulValue(
      formData?.policyNumber,
      formData?.policy_number,
      insuranceRecord?.policy_number,
      insuranceRecord?.policyNumber
    ),
    coverageType: pickFirstMeaningfulValue(
      formData?.coverageType,
      formData?.coverage_type,
      insuranceRecord?.coverage_type,
      insuranceRecord?.coverageType
    ),
    coverageAmount: pickFirstMeaningfulValue(
      formData?.coverageAmount,
      formData?.coverage_amount,
      insuranceRecord?.coverage_amount,
      insuranceRecord?.coverageAmount
    ),
    premiumAmount: pickFirstMeaningfulValue(
      formData?.premiumAmount,
      formData?.premium_amount,
      formData?.insuranceCost,
      formData?.insurance_cost,
      insuranceRecord?.premium_amount,
      insuranceRecord?.premiumAmount,
      insuranceRecord?.insurance_cost
    ),
    insuranceStartDate: pickFirstMeaningfulValue(
      formData?.insuranceStartDate,
      formData?.insurance_start_date,
      insuranceRecord?.start_date,
      insuranceRecord?.startDate
    ),
    insuranceExpiryDate: pickFirstMeaningfulValue(
      formData?.insuranceExpiryDate,
      formData?.insurance_expiry_date,
      insuranceRecord?.expiry_date,
      insuranceRecord?.expiryDate,
      equipment?.nextServiceDue
    ),

    insuranceCost: pickFirstMeaningfulValue(
      formData?.insuranceCost,
      formData?.insurance_cost,
      formData?.premiumAmount,
      formData?.premium_amount,
      insuranceRecord?.insurance_cost,
      insuranceRecord?.premium_amount,
      insuranceRecord?.premiumAmount
    ),
    insuranceStatus: pickFirstMeaningfulValue(
      formData?.insuranceStatus,
      formData?.insurance_status,
      insuranceRecord?.status,
      insuranceRecord?.insurance_status
    ),
    maintenanceCost: pickFirstMeaningfulValue(
      formData?.maintenanceCost,
      formData?.maintenance_cost,
      equipment?.maintenanceCost,
      equipment?.maintenance_cost
    ),
    tyreCost: pickFirstMeaningfulValue(
      formData?.tyreCost,
      formData?.tyre_cost,
      equipment?.tyreCost,
      equipment?.tyre_cost
    ),
    avgCostPerTyre: pickFirstMeaningfulValue(
      formData?.avgCostPerTyre,
      formData?.avg_cost_per_tyre,
      formData?.averageCostPerTyre,
      formData?.average_cost_per_tyre,
      equipment?.avgCostPerTyre,
      equipment?.avg_cost_per_tyre,
      equipment?.averageCostPerTyre,
      equipment?.average_cost_per_tyre
    ),

    attachments: Array.isArray(formData?.attachments) ? formData.attachments : [],
    linkedVehicleId:
      normalizeLinkedValue(formData?.linkedVehicleId) ||
      normalizeLinkedValue(equipment?.linkedVehicleId),
    notes: pickFirstMeaningfulValue(formData?.notes, equipment?.notes),
  };
};

const detailsSchema = Yup.object().shape({
  assetNumber: Yup.string().required("Asset Number is required"),
  equipmentType: Yup.string().required("Equipment Type is required"),
  serialNumber: Yup.string().required("Serial / Chassis No. is required"),
  registrationNumber: Yup.string().required("Registration Number is required"),
  status: Yup.string().required("Status is required"),
  description: Yup.string()
    .test("description-max", "Description too long", (value) => stripHtml(value).length <= 1000)
    .nullable(),
});

const financialInsuranceSchema = Yup.object().shape({
  purchaseDate: Yup.string().required("Purchase Date is required"),
  purchaseCost: Yup.number()
    .typeError("Purchase Cost must be numeric")
    .min(0, "Purchase Cost cannot be negative")
    .required("Purchase Cost is required"),
  paymentType: Yup.string().required("Payment Type is required"),
  depreciationMethod: Yup.string().required("Depreciation Method is required"),
  usefulLife: Yup.number()
    .typeError("Useful Life must be numeric")
    .positive("Useful Life must be greater than zero")
    .required("Useful Life is required"),
  residualValue: Yup.number()
    .typeError("Residual Value must be numeric")
    .min(0, "Residual Value cannot be negative")
    .required("Residual Value is required"),
  policyNumber: Yup.string().required("Policy Number is required"),
  insuranceProvider: Yup.string().required("Provider is required"),
  coverageType: Yup.string().required("Coverage Type is required"),
  coverageAmount: Yup.number()
    .typeError("Coverage Amount must be numeric")
    .min(0, "Coverage Amount cannot be negative")
    .required("Coverage Amount is required"),
  premiumAmount: Yup.number()
    .typeError("Premium must be numeric")
    .min(0, "Premium cannot be negative")
    .required("Premium is required"),
  insuranceStartDate: Yup.string().required("Start Date is required"),
  insuranceExpiryDate: Yup.string().required("Expiry Date is required"),
});

const EquipmentDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdate = hasPermission({
    permissionSlug: AssetsPermission.UpdateEquipment,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const location = useLocation();
  const { systemId } = useParams();
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "equipments")
  );
  const {
    equipmentDetailData,
    equipmentDetailLoading,
    equipmentInstallmentsData = [],
    equipmentInstallmentsLoading = false,
    createEquipmentInstallmentLoading = false,
    equipmentDistanceSummaryData = null,
    equipmentDistanceSummaryLoading = false,
    equipmentTripsData = [],
    equipmentTripsLoading = false,
    equipmentTripsMeta = {},
    equipmentServiceRecordsData,
    equipmentServiceRecordsLoading,
    equipmentServiceRecordsMeta,
    equipmentHistoryData,
    equipmentHistoryLoading,
    equipmentAssignmentHistoryData,
    equipmentAssignmentHistoryLoading,
    attachEquipmentTyresLoading,
    updateEquipmentPurchaseInfoLoading,
    equipmentDropdownData = [],
    equipmentTypesDropdownData,
    equipmentManufacturersDropdownData,
    equipmentModelsDropdownData,
    equipmentVendorsDropdownData,
  } = useSelector((state) => state.equipment);
  const {
    banksDropdownData = [],
    loading: dropdownLoading = false,
  } = useSelector((state) => state.dropdowns || {});
  const {
    tyresDropdownData = null,
    dropdownLoadingCount: tyreDropdownLoadingCount = 0,
  } = useSelector((state) => state.tyre || {});
  const {
    attachVehicleEquipmentLoading = false,
    detachVehicleTyreLoading = false,
  } = useSelector((state) => state.services || {});

  const [activeTab, setActiveTab] = useState("general_info");
  const [commentInput, setCommentInput] = useState("");
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState(defaultEquipmentActivities);
  const [showSwapTyresModal, setShowSwapTyresModal] = useState(false);
  const [showAddInstallmentModal, setShowAddInstallmentModal] = useState(false);
  const [attachmentViewer, setAttachmentViewer] = useState({
    open: false,
    urls: [],
    currentIndex: 0,
  });
  const [swapTyresLoading, setSwapTyresLoading] = useState(false);
  const [sourceAssetTyreOptions, setSourceAssetTyreOptions] = useState([]);
  const [sourceAssetTyresLoading, setSourceAssetTyresLoading] = useState(false);
  const [targetAssetTyreOptions, setTargetAssetTyreOptions] = useState([]);
  const [targetAssetTyresLoading, setTargetAssetTyresLoading] = useState(false);
  const [linkedVehicleDraft, setLinkedVehicleDraft] = useState("");
  const [isVehicleSelectionUnlocked, setIsVehicleSelectionUnlocked] = useState(true);
  const [vehiclesWithEquipmentDropdownData, setVehiclesWithEquipmentDropdownData] = useState([]);
  const [linkedVehicleDropdownLoading, setLinkedVehicleDropdownLoading] = useState(false);
  const [detachingTyreFieldKey, setDetachingTyreFieldKey] = useState("");
  const [installmentForm, setInstallmentForm] = useState(createInstallmentFormState);
  const [localInstallmentData, setLocalInstallmentData] = useState([]);
  const [swapTyreForm, setSwapTyreForm] = useState(createSwapTyreFormState);

  const [servicePage, setServicePage] = useState(1);
  const [serviceLimit, setServiceLimit] = useState(10);
  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehicleLimit, setVehicleLimit] = useState(10);
  const [tyrePage, setTyrePage] = useState(1);
  const [tyreLimit, setTyreLimit] = useState(10);
  const [installmentPage, setInstallmentPage] = useState(1);
  const [installmentLimit, setInstallmentLimit] = useState(10);
  const [tripPage, setTripPage] = useState(1);
  const [tripLimit, setTripLimit] = useState(10);

  const currentUser = useMemo(() => getCurrentUser(), []);

  const openAttachmentViewer = (files = []) => {
    const urls = resolveInstallmentAttachmentUrls(files);

    if (!urls.length) {
      errorNotify("No attachments found");
      return;
    }

    setAttachmentViewer({
      open: true,
      urls,
      currentIndex: 0,
    });
  };

  const closeAttachmentViewer = () => {
    setAttachmentViewer({
      open: false,
      urls: [],
      currentIndex: 0,
    });
  };

  const changeAttachmentSlide = (direction) => {
    setAttachmentViewer((previous) => {
      const total = previous.urls.length;
      if (!total) return previous;

      return {
        ...previous,
        currentIndex: (previous.currentIndex + direction + total) % total,
      };
    });
  };

  const detailEquipmentId = useMemo(
    () => resolveEquipmentDetailId(systemId, location?.state?.equipment),
    [location?.state?.equipment, systemId]
  );

  const allEquipments = useMemo(() => [], []);
  const allVehicles = useMemo(() => [], []);
  const allTyres = useMemo(() => [], []);

  const decodedSystemId = useMemo(
    () => decodeURIComponent(systemId || location?.state?.equipment?.systemId || ""),
    [location?.state?.equipment?.systemId, systemId]
  );

  const equipmentTypeDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentTypesDropdownData)
      ? equipmentTypesDropdownData
      : findCollectionByKeys(equipmentTypesDropdownData, [
          "equipmenttypes",
          "equipmenttype",
          "equipmenttypedropdown",
          "types",
        ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "equipment_type_id"],
        labelKeys: ["label", "equipment_type_name", "name", "title"],
        defaultLabelPrefix: "Equipment Type",
      })
    );
  }, [equipmentTypesDropdownData]);

  const manufacturerDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentManufacturersDropdownData)
      ? equipmentManufacturersDropdownData
      : findCollectionByKeys(equipmentManufacturersDropdownData, [
          "equipmentmanufacturers",
          "equipmentmanufacturer",
          "manufacturers",
          "manufacturer",
        ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "manufacturer_id"],
        labelKeys: ["label", "manufacturer_name", "name", "title"],
        defaultLabelPrefix: "Manufacturer",
      })
    );
  }, [equipmentManufacturersDropdownData]);

  const modelDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentModelsDropdownData)
      ? equipmentModelsDropdownData
      : findCollectionByKeys(equipmentModelsDropdownData, [
          "equipmentmodels",
          "equipmentmodel",
          "models",
          "model",
        ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "model_id"],
        labelKeys: ["label", "model_name", "name", "title"],
        defaultLabelPrefix: "Model",
      })
    );
  }, [equipmentModelsDropdownData]);

  const supplierVendorDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentVendorsDropdownData)
      ? equipmentVendorsDropdownData
      : findCollectionByKeys(equipmentVendorsDropdownData, [
          "equipmentvendors",
          "equipmentvendor",
          "suppliervendors",
          "vendors",
          "vendor",
        ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "vendor_id", "supplier_vendor_id"],
        labelKeys: ["title", "label", "vendor_name", "name"],
        defaultLabelPrefix: "Vendor",
      })
    );
  }, [equipmentVendorsDropdownData]);

  const localEquipment = useMemo(() => {
    if (!decodedSystemId) return location?.state?.equipment || null;

    return (
      allEquipments.find(
        (row) =>
          String(row?.systemId || "").toLowerCase() === decodedSystemId.toLowerCase()
      ) ||
      location?.state?.equipment ||
      null
    );
  }, [allEquipments, decodedSystemId, location?.state?.equipment]);

  const mappedEquipmentDetail = useMemo(() => {
    if (!detailEquipmentId) return null;
    if (!equipmentDetailData) return null;

    const detailId = Number(equipmentDetailData?.id);
    if (Number.isFinite(detailId) && detailId > 0 && detailId !== detailEquipmentId) {
      return null;
    }

    return mapEquipmentApiRow(
      equipmentDetailData,
      equipmentTypeDropdownOptions,
      location?.state?.equipment,
      {
        manufacturerOptions: manufacturerDropdownOptions,
        modelOptions: modelDropdownOptions,
        vendorOptions: supplierVendorDropdownOptions,
      }
    );
  }, [
    detailEquipmentId,
    equipmentDetailData,
    equipmentTypeDropdownOptions,
    manufacturerDropdownOptions,
    modelDropdownOptions,
    supplierVendorDropdownOptions,
    location?.state?.equipment,
  ]);

  const equipment = useMemo(() => {
    if (mappedEquipmentDetail && localEquipment?.isCustom) {
      return {
        ...mappedEquipmentDetail,
        ...localEquipment,
        formData: {
          ...(mappedEquipmentDetail.formData || {}),
          ...(localEquipment.formData || {}),
        },
      };
    }

    return mappedEquipmentDetail || localEquipment || null;
  }, [localEquipment, mappedEquipmentDetail]);

  const equipmentApiId = useMemo(() => {
    const candidates = [
      equipmentDetailData?.id,
      mappedEquipmentDetail?.id,
      localEquipment?.id,
      detailEquipmentId,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    return null;
  }, [detailEquipmentId, equipmentDetailData?.id, localEquipment?.id, mappedEquipmentDetail?.id]);

  const canUseEquipmentHistoryApi =
    Number.isFinite(Number(equipmentApiId)) && Number(equipmentApiId) > 0;
  const canUseEquipmentServicesApi = canUseEquipmentHistoryApi;
  const canUseEquipmentInstallmentsApi = canUseEquipmentHistoryApi;
  const canUseEquipmentDistanceSummaryApi = canUseEquipmentHistoryApi;
  const canUseEquipmentTripsApi = canUseEquipmentHistoryApi;

  useEffect(() => {
    dispatch(fetchEquipmentTypesDropdown());
    dispatch(fetchEquipmentManufacturersDropdown());
    dispatch(fetchEquipmentModelsDropdown());
    dispatch(fetchEquipmentVendorsDropdown());
    dispatch(fetchEquipmentDropdown());
    dispatch(fetchBanksDropdown());
    dispatch(fetchTyresDropdown());

    return () => {
      dispatch(clearEquipmentDropdowns());
    };
  }, [dispatch]);

  useEffect(() => {
    let isActive = true;
    setLinkedVehicleDropdownLoading(true);

    apiService
      .getVehiclesDropdownWithEquipment()
      .then((response) => {
        if (!isActive) return;
        setVehiclesWithEquipmentDropdownData(response?.data || []);
      })
      .catch(() => {
        if (!isActive) return;
        setVehiclesWithEquipmentDropdownData([]);
      })
      .finally(() => {
        if (!isActive) return;
        setLinkedVehicleDropdownLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!detailEquipmentId) {
      dispatch(clearEquipmentDetailState());
      dispatch(clearEquipmentHistoryState());
      dispatch(clearEquipmentAssignmentHistoryState());
      return undefined;
    }

    dispatch(fetchEquipmentDetail(detailEquipmentId));

    return () => {
      dispatch(clearEquipmentDetailState());
      dispatch(clearEquipmentHistoryState());
      dispatch(clearEquipmentAssignmentHistoryState());
    };
  }, [detailEquipmentId, dispatch]);

  useEffect(() => {
    if (!canUseEquipmentHistoryApi) return;
    dispatch(fetchEquipmentAssignmentHistory(equipmentApiId));
  }, [canUseEquipmentHistoryApi, dispatch, equipmentApiId]);

  useEffect(() => {
    if (!canUseEquipmentServicesApi) return;

    dispatch(
      fetchEquipmentServiceRecords({
        equipment_id: equipmentApiId,
        page: servicePage,
        limit: serviceLimit,
      })
    );
  }, [
    canUseEquipmentServicesApi,
    dispatch,
    equipmentApiId,
    serviceLimit,
    servicePage,
  ]);

  useEffect(() => {
    if (!canUseEquipmentInstallmentsApi) return;

    dispatch(fetchEquipmentInstallments(equipmentApiId));
  }, [canUseEquipmentInstallmentsApi, dispatch, equipmentApiId]);

  useEffect(() => {
    if (!canUseEquipmentDistanceSummaryApi) return;

    dispatch(fetchEquipmentDistanceSummary(equipmentApiId));
  }, [canUseEquipmentDistanceSummaryApi, dispatch, equipmentApiId]);

  useEffect(() => {
    if (!canUseEquipmentTripsApi) return;

    dispatch(
      fetchEquipmentTrips({
        equipment_id: equipmentApiId,
        page: tripPage,
        limit: tripLimit,
      })
    );
  }, [
    canUseEquipmentTripsApi,
    dispatch,
    equipmentApiId,
    tripLimit,
    tripPage,
  ]);

  const handleFetchEquipmentHistory = useCallback(
    async (historyType = "all") => {
      if (!canUseEquipmentHistoryApi) return;
      const normalizedEquipmentId = parseInt(equipmentApiId, 10);
      if (!Number.isFinite(normalizedEquipmentId) || normalizedEquipmentId <= 0) return;

      await dispatch(
        fetchEquipmentHistory({
          history_type: historyType,
          equipment_id: normalizedEquipmentId,
        })
      );
    },
    [canUseEquipmentHistoryApi, dispatch, equipmentApiId]
  );

  const handleSubmitEquipmentComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!canUseEquipmentHistoryApi) return;
      const normalizedEquipmentId = parseInt(equipmentApiId, 10);
      if (!Number.isFinite(normalizedEquipmentId) || normalizedEquipmentId <= 0) return;

      await dispatch(
        commentOnEquipment({
          equipment_id: normalizedEquipmentId,
          comment,
          parent_id,
          mention_user_ids,
        })
      ).unwrap();
    },
    [canUseEquipmentHistoryApi, dispatch, equipmentApiId]
  );

  useEffect(() => {
    setComments(Array.isArray(equipment?.formData?.comments) ? equipment.formData.comments : []);
    setActivities(
      Array.isArray(equipment?.formData?.activities) && equipment.formData.activities.length
        ? equipment.formData.activities
        : defaultEquipmentActivities
    );
    setCommentInput("");
  }, [equipment?.systemId]);

  useEffect(() => {
    setShowSwapTyresModal(false);
    setShowAddInstallmentModal(false);
    setSwapTyresLoading(false);
    setSourceAssetTyreOptions([]);
    setSourceAssetTyresLoading(false);
    setTargetAssetTyreOptions([]);
    setTargetAssetTyresLoading(false);
    setSwapTyreForm(createSwapTyreFormState());
    setInstallmentForm(createInstallmentFormState());

    const source = equipment?.formData?.installmentSchedule;
    if (Array.isArray(source)) {
      setLocalInstallmentData(source);
      return;
    }

    setLocalInstallmentData(buildInstallmentScheduleData(equipment?.formData));
  }, [equipment?.systemId]);

  const initialValues = useMemo(() => getInitialValues(equipment), [equipment]);
  const existingGeneralInfoIds = useMemo(
    () => ({
      equipmentTypeId: getPositiveNumber(
        equipment?.formData?.equipmentTypeId,
        equipmentDetailData?.equipment_type_id
      ),
      manufacturerId: getPositiveNumber(
        equipment?.formData?.manufacturerId,
        equipmentDetailData?.manufacturer_id
      ),
      modelId: getPositiveNumber(
        equipment?.formData?.modelId,
        equipmentDetailData?.model_id
      ),
      supplierVendorId: getPositiveNumber(
        equipment?.formData?.supplierVendorId,
        equipment?.formData?.vendorId,
        equipmentDetailData?.supplier_vendor_id
      ),
    }),
    [
      equipment?.formData?.equipmentTypeId,
      equipment?.formData?.manufacturerId,
      equipment?.formData?.modelId,
      equipment?.formData?.supplierVendorId,
      equipment?.formData?.vendorId,
      equipmentDetailData?.equipment_type_id,
      equipmentDetailData?.manufacturer_id,
      equipmentDetailData?.model_id,
      equipmentDetailData?.supplier_vendor_id,
    ]
  );

  const serviceRows = useMemo(() => {
    if (canUseEquipmentServicesApi) {
      return mapEquipmentServiceRows(equipmentServiceRecordsData);
    }

    const source = equipment?.formData?.serviceRecords;
    return Array.isArray(source) && source.length ? source : baseServiceRows;
  }, [
    canUseEquipmentServicesApi,
    equipment?.formData?.serviceRecords,
    equipmentServiceRecordsData,
  ]);

  const installmentData = useMemo(() => {
    if (canUseEquipmentInstallmentsApi) {
      return mapEquipmentInstallmentRows(equipmentInstallmentsData);
    }

    return localInstallmentData;
  }, [
    canUseEquipmentInstallmentsApi,
    equipmentInstallmentsData,
    localInstallmentData,
  ]);

  const assignmentHistoryPayload = useMemo(() => {
    const source = equipmentAssignmentHistoryData?.data || equipmentAssignmentHistoryData || {};
    return source && typeof source === "object" ? source : {};
  }, [equipmentAssignmentHistoryData]);

  const assignmentHistoryVehicleRecords = useMemo(() => {
    const source =
      (Array.isArray(assignmentHistoryPayload?.vehicles) && assignmentHistoryPayload.vehicles) ||
      findCollectionByKeys(assignmentHistoryPayload, [
        "vehicles",
        "vehiclehistory",
        "vehicleassignmenthistory",
        "equipmentinstalledhistory",
      ]) ||
      [];

    return Array.isArray(source) ? source : [];
  }, [assignmentHistoryPayload]);

  const assignmentHistoryEquipmentAxles = useMemo(() => {
    const source =
      (Array.isArray(assignmentHistoryPayload?.equipmentAxles) &&
        assignmentHistoryPayload.equipmentAxles) ||
      findCollectionByKeys(assignmentHistoryPayload, [
        "equipmentaxles",
        "assetaxles",
        "axles",
        "equipment_axles",
        "asset_axles",
      ]) ||
      [];

    return Array.isArray(source) ? source : [];
  }, [assignmentHistoryPayload]);

  const assignmentHistoryTyreRecords = useMemo(() => {
    const source =
      (Array.isArray(assignmentHistoryPayload?.tyres) && assignmentHistoryPayload.tyres) ||
      findCollectionByKeys(assignmentHistoryPayload, [
        "tyres",
        "tyrehistory",
        "tyreinstalledhistory",
        "tyre_installed_history",
      ]) ||
      [];

    return Array.isArray(source) ? source : [];
  }, [assignmentHistoryPayload]);

  const assignmentEquipmentAxleConfigurations = useMemo(() => {
    if (canUseEquipmentHistoryApi && equipmentAssignmentHistoryData) {
      return mapEquipmentAxleConfigurations(assignmentHistoryEquipmentAxles);
    }

    const source = equipment?.formData?.axleConfigurations;
    return Array.isArray(source) ? source : [];
  }, [
    assignmentHistoryEquipmentAxles,
    canUseEquipmentHistoryApi,
    equipment?.formData?.axleConfigurations,
    equipmentAssignmentHistoryData,
  ]);

  const linkedTyreAssignmentFields = useMemo(() => {
    const linkedFields = new Set();

    assignmentEquipmentAxleConfigurations.forEach((axle, index) => {
      const axleKey = String(axle?.axleId || `A${index + 1}`);

      Object.entries(axle?.assignments || {}).forEach(([fieldKey, value]) => {
        if (normalizeLinkedValue(value)) {
          linkedFields.add(`${axleKey}:${fieldKey}`);
        }
      });
    });

    return linkedFields;
  }, [assignmentEquipmentAxleConfigurations]);

  const currentLinkedVehicleRecord = useMemo(
    () =>
      assignmentHistoryVehicleRecords.find(
        (item) =>
          Boolean(item?.current) ||
          (getPositiveNumber(item?.vehicle_id) && !item?.removed_at && !item?.end_date)
      ) || null,
    [assignmentHistoryVehicleRecords]
  );

  const linkedVehicleOptions = useMemo(() => {
    const source = Array.isArray(vehiclesWithEquipmentDropdownData)
      ? vehiclesWithEquipmentDropdownData
      : findCollectionByKeys(vehiclesWithEquipmentDropdownData, [
          "vehicles",
          "vehicledropdown",
          "vehicle",
          "vehicleswithequipment",
          "vehiclewithequipment",
        ]);
    const currentVehicleId = getPositiveNumber(currentLinkedVehicleRecord?.vehicle_id);
    const currentEquipmentId = getPositiveNumber(equipmentApiId);

    const baseOptions = toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "vehicle_id"],
        labelKeys: ["label", "vehicle_id", "registration_number", "title", "name"],
        defaultLabelPrefix: "Vehicle",
      })
    ).map((option) => {
      const optionVehicleId = getPositiveNumber(option?.optionId, option?.id, option?.value);
      const linkedEquipmentId = getLinkedVehicleEquipmentId(option);

      return {
        ...option,
        linkedEquipmentId,
        systemId: String(
          option?.systemId ||
            option?.label ||
            option?.vehicle_id ||
            option?.registration_number ||
            option?.value ||
            ""
        ).trim(),
        registrationNumber: String(
          option?.registrationNumber ||
            option?.registration_number ||
            option?.label ||
            option?.value ||
            ""
        ).trim(),
        isDisabled:
          Boolean(linkedEquipmentId) &&
          linkedEquipmentId !== currentEquipmentId &&
          optionVehicleId !== currentVehicleId,
      };
    });

    const hasCurrentOption = baseOptions.some(
      (option) => getPositiveNumber(option?.optionId, option?.id, option?.value) === currentVehicleId
    );
    const fallbackLinkedVehicle =
      normalizeLinkedValue(equipment?.linkedVehicleId) ||
      normalizeLinkedValue(equipment?.formData?.linkedVehicleId) ||
      (currentVehicleId ? String(currentVehicleId) : "");

    if (currentVehicleId && !hasCurrentOption) {
      return [
        {
          label: fallbackLinkedVehicle || `Vehicle ${currentVehicleId}`,
          value: fallbackLinkedVehicle || `Vehicle ${currentVehicleId}`,
          optionId: currentVehicleId,
          systemId: fallbackLinkedVehicle || `Vehicle ${currentVehicleId}`,
          registrationNumber: fallbackLinkedVehicle || `Vehicle ${currentVehicleId}`,
          isDisabled: false,
        },
        ...baseOptions,
      ];
    }

    if (baseOptions.length) return baseOptions;

    return allVehicles.map((row) => ({
      label: row?.systemId || "-",
      value: row?.registrationNumber || row?.systemId || "",
      optionId: getPositiveNumber(row?.id),
      systemId: row?.systemId || "",
      registrationNumber: row?.registrationNumber || "",
      isDisabled: false,
    }));
  }, [
    allVehicles,
    currentLinkedVehicleRecord?.vehicle_id,
    equipment?.formData?.linkedVehicleId,
    equipment?.linkedVehicleId,
    equipmentApiId,
    vehiclesWithEquipmentDropdownData,
  ]);

  const currentLinkedVehicleOption = useMemo(() => {
    const currentVehicleId = getPositiveNumber(currentLinkedVehicleRecord?.vehicle_id);
    if (!currentVehicleId) return null;

    return (
      linkedVehicleOptions.find(
        (option) => getPositiveNumber(option?.optionId, option?.id, option?.value) === currentVehicleId
      ) || null
    );
  }, [currentLinkedVehicleRecord?.vehicle_id, linkedVehicleOptions]);

  const draftLinkedVehicleOption = useMemo(
    () => findOption(linkedVehicleOptions, linkedVehicleDraft),
    [linkedVehicleDraft, linkedVehicleOptions]
  );

  useEffect(() => {
    if (currentLinkedVehicleOption?.value) {
      setLinkedVehicleDraft(currentLinkedVehicleOption.value);
      setIsVehicleSelectionUnlocked(false);
      return;
    }

    if (canUseEquipmentHistoryApi && equipmentAssignmentHistoryData) {
      setLinkedVehicleDraft("");
      setIsVehicleSelectionUnlocked(true);
      return;
    }

    const fallbackLinkedVehicle = normalizeLinkedValue(
      equipment?.linkedVehicleId || equipment?.formData?.linkedVehicleId
    );
    if (fallbackLinkedVehicle) {
      setLinkedVehicleDraft((previous) => previous || fallbackLinkedVehicle);
    }
    setIsVehicleSelectionUnlocked(true);
  }, [
    canUseEquipmentHistoryApi,
    currentLinkedVehicleOption?.value,
    equipment?.formData?.linkedVehicleId,
    equipment?.linkedVehicleId,
    equipmentAssignmentHistoryData,
  ]);

  useEffect(() => {
    if (!showSwapTyresModal) {
      setSourceAssetTyreOptions([]);
      setSourceAssetTyresLoading(false);
      return undefined;
    }

    const sourceAssetId = getPositiveNumber(swapTyreForm.sourceAssetId);
    const sourceAssetType = normalizeSwapAssetType(swapTyreForm.sourceType);
    if (!sourceAssetId) {
      setSourceAssetTyreOptions([]);
      setSourceAssetTyresLoading(false);
      return undefined;
    }

    let isActive = true;
    setSourceAssetTyresLoading(true);
    setSourceAssetTyreOptions([]);

    apiService
      .getAssetTyres({
        asset_id: sourceAssetId,
        asset_type: sourceAssetType,
      })
      .then((response) => {
        if (!isActive) return;
        setSourceAssetTyreOptions(mapAssetTyreOptions(response?.data));
      })
      .catch(() => {
        if (!isActive) return;
        setSourceAssetTyreOptions([]);
      })
      .finally(() => {
        if (!isActive) return;
        setSourceAssetTyresLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [showSwapTyresModal, swapTyreForm.sourceAssetId, swapTyreForm.sourceType]);

  useEffect(() => {
    if (!showSwapTyresModal) {
      setTargetAssetTyreOptions([]);
      setTargetAssetTyresLoading(false);
      return undefined;
    }

    const targetAssetId = getPositiveNumber(swapTyreForm.targetAssetId);
    const targetAssetType = normalizeSwapAssetType(swapTyreForm.targetType);
    if (!targetAssetId || !String(swapTyreForm.targetType || "").trim()) {
      setTargetAssetTyreOptions([]);
      setTargetAssetTyresLoading(false);
      return undefined;
    }

    let isActive = true;
    setTargetAssetTyresLoading(true);
    setTargetAssetTyreOptions([]);

    apiService
      .getAssetTyres({
        asset_id: targetAssetId,
        asset_type: targetAssetType,
      })
      .then((response) => {
        if (!isActive) return;
        setTargetAssetTyreOptions(mapAssetTyreOptions(response?.data));
      })
      .catch(() => {
        if (!isActive) return;
        setTargetAssetTyreOptions([]);
      })
      .finally(() => {
        if (!isActive) return;
        setTargetAssetTyresLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [showSwapTyresModal, swapTyreForm.targetAssetId, swapTyreForm.targetType]);

  const equipmentSwapAssetOptions = useMemo(
    () => {
      const source = Array.isArray(equipmentDropdownData)
        ? equipmentDropdownData
        : findCollectionByKeys(equipmentDropdownData, [
            "equipments",
            "equipmentdropdown",
            "equipment",
          ]);

      const baseOptions = toLabelValueOptions(
        normalizeOptions(source, {
          valueKeys: ["value", "id", "equipment_id"],
          labelKeys: ["label", "equipment_id", "title", "name", "serial_number"],
          defaultLabelPrefix: "Equipment",
        })
      ).map((option) => ({
        ...option,
        systemId: String(
          option?.systemId ||
            option?.equipment_id ||
            option?.label ||
            option?.value ||
            ""
        ).trim(),
      }));

      if (baseOptions.length) return baseOptions;

      return allEquipments.map((row) => ({
        label: row?.systemId || "-",
        value: row?.systemId || "",
        systemId: row?.systemId || "",
      }));
    },
    [allEquipments, equipmentDropdownData]
  );

  const tyreOptions = useMemo(() => {
    const source = Array.isArray(tyresDropdownData)
      ? tyresDropdownData
      : findCollectionByKeys(tyresDropdownData, [
          "tyres",
          "tyre",
          "tyresdropdown",
          "tyredropdown",
          "tyrelist",
        ]);

    const baseOptions = toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "tyre_id", "system_id"],
        labelKeys: ["label", "tyres_id", "tyre_id", "serial_number", "system_id", "name", "title"],
        defaultLabelPrefix: "Tyre",
      })
    );

    const assignedTyreIds = new Set();
    assignmentHistoryEquipmentAxles.forEach((axle) => {
      [
        axle?.outer_left_tyre_id,
        axle?.outer_right_tyre_id,
        axle?.inner_left_tyre_id,
        axle?.inner_right_tyre_id,
        axle?.left_tyre_id,
        axle?.right_tyre_id,
        axle?.left_inner_tyre_id,
        axle?.left_outer_tyre_id,
        axle?.right_inner_tyre_id,
        axle?.right_outer_tyre_id,
      ].forEach((value) => {
        const tyreId = getPositiveNumber(value);
        if (tyreId) assignedTyreIds.add(tyreId);
      });
    });

    const fallbackOptions = Array.from(assignedTyreIds)
      .filter(
        (tyreId) =>
          !baseOptions.some(
            (option) => getPositiveNumber(option?.optionId, option?.id, option?.value) === tyreId
          )
      )
      .map((tyreId) => ({
        label: `TY-${tyreId}`,
        value: String(tyreId),
        optionId: tyreId,
      }));

    if (baseOptions.length || fallbackOptions.length) {
      return [...fallbackOptions, ...baseOptions];
    }

    return allTyres.map((row) => ({
      label: row?.systemId || "-",
      value: row?.systemId || "",
      optionId: getPositiveNumber(row?.id),
    }));
  }, [allTyres, assignmentHistoryEquipmentAxles, tyresDropdownData]);

  const tyreDropdownLoading = tyreDropdownLoadingCount > 0;

  const paginatedServiceRows = useMemo(() => {
    if (canUseEquipmentServicesApi) return serviceRows;

    const start = (servicePage - 1) * serviceLimit;
    return serviceRows.slice(start, start + serviceLimit);
  }, [canUseEquipmentServicesApi, serviceRows, serviceLimit, servicePage]);

  const serviceTotalItems = useMemo(() => {
    if (!canUseEquipmentServicesApi) return serviceRows.length;

    return (
      Number(equipmentServiceRecordsMeta?.totalItems) ||
      Number(equipmentServiceRecordsMeta?.itemCount) ||
      serviceRows.length
    );
  }, [canUseEquipmentServicesApi, equipmentServiceRecordsMeta, serviceRows.length]);

  const serviceTotalPages = useMemo(() => {
    if (!canUseEquipmentServicesApi) {
      return Math.max(1, Math.ceil(serviceRows.length / Math.max(serviceLimit, 1)));
    }

    return Math.max(
      1,
      Number(equipmentServiceRecordsMeta?.totalPages) ||
        Math.ceil(serviceTotalItems / Math.max(serviceLimit, 1))
    );
  }, [
    canUseEquipmentServicesApi,
    equipmentServiceRecordsMeta,
    serviceLimit,
    serviceRows.length,
    serviceTotalItems,
  ]);

  const paginatedInstallmentRows = useMemo(() => {
    const start = (installmentPage - 1) * installmentLimit;
    return installmentData.slice(start, start + installmentLimit);
  }, [installmentData, installmentLimit, installmentPage]);

  const tripData = useMemo(() => {
    if (canUseEquipmentTripsApi) {
      return mapEquipmentTripRows(equipmentTripsData);
    }

    return [
      { id: "TRIP-001", driverName: "Noman Aslam", driverImg: "https://i.pravatar.cc/150?u=noman", startDate: "2025-05-15", endDate: "2025-05-18", distance: 600 },
      { id: "TRIP-002", driverName: "Waqar Siddiq", driverImg: "https://i.pravatar.cc/150?u=waqar", startDate: "2025-06-15", endDate: "2025-06-16", distance: 485 },
      { id: "TRIP-003", driverName: "Sajid Khan", driverImg: "https://i.pravatar.cc/150?u=sajid", startDate: "2025-07-15", endDate: "2025-07-15", distance: 200 },
    ];
  }, [canUseEquipmentTripsApi, equipmentTripsData]);

  const paginatedTripRows = useMemo(() => {
    if (canUseEquipmentTripsApi) return tripData;

    const start = (tripPage - 1) * tripLimit;
    return tripData.slice(start, start + tripLimit);
  }, [canUseEquipmentTripsApi, tripData, tripLimit, tripPage]);

  const tripTotalItems = useMemo(() => {
    if (!canUseEquipmentTripsApi) return tripData.length;

    return (
      Number(equipmentTripsMeta?.totalItems) ||
      Number(equipmentTripsMeta?.itemCount) ||
      tripData.length
    );
  }, [canUseEquipmentTripsApi, equipmentTripsMeta, tripData.length]);

  const tripTotalPages = useMemo(() => {
    if (!canUseEquipmentTripsApi) {
      return Math.max(1, Math.ceil(tripData.length / Math.max(tripLimit, 1)));
    }

    return Math.max(
      1,
      Number(equipmentTripsMeta?.totalPages) ||
        Math.ceil(tripTotalItems / Math.max(tripLimit, 1))
    );
  }, [
    canUseEquipmentTripsApi,
    equipmentTripsMeta,
    tripData.length,
    tripLimit,
    tripTotalItems,
  ]);

  const totalOperationalKm = useMemo(
    () => tripData.reduce((sum, row) => sum + normalizeNumber(row.distanceValue ?? row.distance), 0),
    [tripData]
  );

  const operationalSummary = useMemo(() => {
    if (canUseEquipmentDistanceSummaryApi && equipmentDistanceSummaryData) {
      return {
        totalKm: normalizeNumber(
          equipmentDistanceSummaryData?.total_km,
          totalOperationalKm
        ),
        averageKmPerMonth: normalizeNumber(
          equipmentDistanceSummaryData?.average_km_per_month,
          0
        ),
        last30DaysKm: normalizeNumber(
          equipmentDistanceSummaryData?.last_30_days_km,
          0
        ),
      };
    }

    const source = equipment?.formData?.operationalSummary || {};
    return {
      totalKm: normalizeNumber(
        source?.totalKm,
        equipment?.formData?.usageMeterReadingValue || totalOperationalKm
      ),
      averageKmPerMonth: normalizeNumber(source?.averageKmPerMonth, 0),
      last30DaysKm: normalizeNumber(source?.last30DaysKm, 0),
    };
  }, [
    canUseEquipmentDistanceSummaryApi,
    equipment?.formData?.operationalSummary,
    equipment?.formData?.usageMeterReadingValue,
    equipmentDistanceSummaryData,
    totalOperationalKm,
  ]);

  const serviceColumns = [
    {
      name: "Service Entry ID",
      selector: (row) => <span className="hyperlink" title={row.id}>{row.id}</span>,
      tableColumn: false,
      minWidth: "155px",
    },
    {
      name: "Work Order ID",
      selector: (row) => <span className="hyperlink" title={row.workOrderId}>{row.workOrderId}</span>,
      tableColumn: false,
      minWidth: "145px",
    },
    { name: "Service Type", selector: (row) => row.serviceType, tableColumn: false, minWidth: "130px" },
    { name: "Service Date", selector: (row) => row.serviceDate, tableColumn: false, minWidth: "130px" },
    { name: "Odometer", selector: (row) => row.odometer, tableColumn: false, minWidth: "110px" },
    {
      name: "Part Cost (PKR)",
      selector: (row) => formatAmount(row.partCost),
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Labour Cost (PKR)",
      selector: (row) => formatAmount(row.labourCost),
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Total Cost (PKR)",
      selector: (row) => formatAmount(row.totalCost),
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Status",
      selector: (row) => {
        const meta = serviceStatusMeta(row.status);
        return (
          <span className="asset_vehicle_table_status" style={{ color: meta.color }}>
            {meta.icon}
            {meta.label}
          </span>
        );
      },
      tableColumn: false,
      minWidth: "130px",
    },
  ];

  const vehicleColumns = [
    {
      name: "Vehicle ID",
      selector: (row) => <span className="hyperlink">{row.equipmentId}</span>,
      tableColumn: false,
      minWidth: "130px",
    },
    { name: "Start Date", selector: (row) => row.startDate, tableColumn: false, minWidth: "120px" },
    { name: "End Date", selector: (row) => row.endDate, tableColumn: false, minWidth: "120px" },
    { name: "Duration", selector: (row) => row.duration, tableColumn: false, minWidth: "180px" },
  ];

  const tyreColumns = [
    {
      name: "Tyre ID",
      selector: (row) => <span className="hyperlink">{row.tyreId}</span>,
      tableColumn: false,
      minWidth: "120px",
    },
    { name: "Position", selector: (row) => row.position, tableColumn: false, minWidth: "140px" },
    {
      name: "Installed Date",
      selector: (row) => row.installedDate,
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Removed Date",
      selector: (row) => row.removedDate,
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Start Odometer",
      selector: (row) => row.startOdometer,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "End Odometer",
      selector: (row) => row.endOdometer,
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "KM Driven",
      selector: (row) => row.kmDriven || "-",
      tableColumn: false,
      minWidth: "130px",
    },
  ];

  const installmentColumns = [
    {
      name: "Installment #",
      selector: (row) => row.number,
      tableColumn: false,
      minWidth: "110px",
    },
    {
      name: "Payment Method",
      selector: (row) => row.paymentMethod || "-",
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Amount (PKR)",
      selector: (row) => formatAmount(row.amount),
      tableColumn: false,
      minWidth: "120px",
    },
    { name: "submission Date", selector: (row) => row.dueDate, tableColumn: false, minWidth: "140px" },
    {
      name: "Attachments",
      selector: (row) => row.attachmentsCount ? (
        <button
          type="button"
          className="d-flex align-items-center gap-1"
          style={{
            color: "#3d18c3",
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
          }}
          onClick={(event) => {
            event.stopPropagation();
            openAttachmentViewer(row.attachments);
          }}
          title="View attachments"
        >
          <FiPaperclip /> {row.attachmentsCount}
        </button>
      ) : "",
      tableColumn: false,
      minWidth: "120px",
      ignoreRowClick: true,
    },
  ];

  const tripColumns = [
    {
      name: "Trip ID",
      selector: (row) => <span className="hyperlink">{row.id}</span>,
      tableColumn: false,
      minWidth: "120px",
    },
    {
      name: "Driver",
      selector: (row) => (
        <div className="asset_driver_cell d-flex align-items-center gap-2">
          <img src={row.driverImg} className="user_icon_img rounded-circle" alt="Driver" />
          <span className="hyperlink">{row.driverName}</span>
        </div>
      ),
      tableColumn: false,
      minWidth: "180px",
    },
    { name: "Start Date", selector: (row) => row.startDate, tableColumn: false, minWidth: "130px" },
    { name: "End Date", selector: (row) => row.endDate, tableColumn: false, minWidth: "130px" },
    { name: "Distance (km)", selector: (row) => row.distance, tableColumn: false, minWidth: "120px" },
  ];

  const handleCommentSend = () => {
    const plainText = stripHtml(commentInput);
    if (!plainText) return;

    const createdAtTs = Date.now();
    const createdAt = new Date(createdAtTs).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const commentItem = {
      id: `comment-${createdAtTs}`,
      type: "comment",
      user: currentUser?.username || currentUser?.name || "User",
      image: getUserImage(currentUser),
      text: commentInput,
      time: createdAt,
      createdAtTs,
    };

    setComments((previous) => [commentItem, ...previous]);
    setCommentInput("");
  };

  if (equipmentDetailLoading && !equipment) {
    return <div className="loading_spinner"><Spinner animation="grow" /></div>;
  }

  if (!equipment) {
    return (
      <div className="assets_vehicle_details_page">
        <Breadcrumbs values={["Assets", "Equipments", "Equipment Details"]} />
        <div className="page_container">
          <div className="table_no_records">Equipment not found</div>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={
        activeTab === "general_info"
          ? detailsSchema
          : activeTab === "financial_insurance"
            ? financialInsuranceSchema
            : Yup.object().shape({})
      }
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        if (!canUpdate) {
          setSubmitting(false);
          errorNotify("You do not have permission to update equipment.");
          return;
        }

        if (!equipmentApiId) {
          setSubmitting(false);
          errorNotify("Equipment ID is missing");
          return;
        }

        if (activeTab === "financial_insurance") {
          const initialPurchaseSnapshot = buildEquipmentPurchaseInfoSnapshot(
            equipmentApiId,
            initialValues
          );
          const currentPurchaseSnapshot = buildEquipmentPurchaseInfoSnapshot(
            equipmentApiId,
            values
          );
          const purchasePayload = {
            ...currentPurchaseSnapshot,
            updatedKeys: getUpdatedEquipmentPurchaseInfoKeys(
              currentPurchaseSnapshot,
              initialPurchaseSnapshot
            ),
          };

          try {
            await dispatch(updateEquipmentPurchaseInfo(purchasePayload)).unwrap();
            setSubmitting(false);
            successNotify("Equipment financial & insurance details updated successfully");
            navigate(returnPath);
          } catch (error) {
            setSubmitting(false);
            errorNotify(error || "Failed to update equipment financial & insurance details");
          }
          return;
        }

        const resolvedIds = {
          equipmentTypeId: resolveSelectOptionId(
            equipmentTypeDropdownOptions,
            values.equipmentType,
            {
              fallbackId: existingGeneralInfoIds.equipmentTypeId,
              initialValue: initialValues.equipmentType,
            }
          ),
          manufacturerId: resolveSelectOptionId(
            manufacturerDropdownOptions,
            values.manufacturer,
            {
              fallbackId: existingGeneralInfoIds.manufacturerId,
              initialValue: initialValues.manufacturer,
            }
          ),
          modelId: resolveSelectOptionId(modelDropdownOptions, values.modelId || values.model, {
            fallbackId: existingGeneralInfoIds.modelId,
            initialValue: initialValues.modelId || initialValues.model,
          }),
          supplierVendorId: resolveSelectOptionId(
            supplierVendorDropdownOptions,
            values.supplierVendorId || values.supplierVendor,
            {
              fallbackId: existingGeneralInfoIds.supplierVendorId,
              initialValue: initialValues.supplierVendorId || initialValues.supplierVendor,
            }
          ),
        };

        const initialSnapshot = buildEquipmentGeneralInfoSnapshot({
          equipmentId: equipmentApiId,
          values: initialValues,
          attachments: initialValues.attachments || [],
          equipmentTypeId: existingGeneralInfoIds.equipmentTypeId,
          manufacturerId: existingGeneralInfoIds.manufacturerId,
          modelId: existingGeneralInfoIds.modelId,
          supplierVendorId: existingGeneralInfoIds.supplierVendorId,
        });
        const currentSnapshot = buildEquipmentGeneralInfoSnapshot({
          equipmentId: equipmentApiId,
          values,
          attachments: values.attachments || [],
          equipmentTypeId: resolvedIds.equipmentTypeId,
          manufacturerId: resolvedIds.manufacturerId,
          modelId: resolvedIds.modelId,
          supplierVendorId: resolvedIds.supplierVendorId,
        });
        const payload = {
          ...currentSnapshot,
          updatedKeys: getUpdatedEquipmentGeneralInfoKeys(
            currentSnapshot,
            initialSnapshot
          ),
        };

        try {
          await dispatch(updateEquipmentGeneralInfo(payload)).unwrap();
          setSubmitting(false);
          successNotify("Equipment details updated successfully");
          navigate(returnPath);
        } catch (error) {
          setSubmitting(false);
          errorNotify(error || "Failed to update equipment details");
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => {
        const currentStatusMeta = statusMeta(values.status);
        const equipmentStatusSelectOptions = ensureCurrentOption(
          equipmentStatusOptions,
          values.status
        );
        const equipmentStatusTopOptions = equipmentStatusSelectOptions.map((option) => ({
          ...option,
          icon: statusMeta(option.value).icon,
        }));
        const purchaseCost = normalizeNumber(
          values.purchaseCost,
          normalizeNumber(equipment?.totalCost, 0)
        );
        const currentAttachmentUrl =
          attachmentViewer.urls[attachmentViewer.currentIndex] || "";
        const currentAttachmentName = getInstallmentAttachmentName(currentAttachmentUrl);
        const hasMultipleAttachments = attachmentViewer.urls.length > 1;
        const insuranceAnnualCost = normalizeNumber(
          values.insuranceCost,
          normalizeNumber(values.premiumAmount, 0)
        );
        const maintenanceAnnualCost = normalizeNumber(
          values.maintenanceCost,
          serviceRows.reduce((sum, row) => sum + normalizeNumber(row?.totalCost), 0)
        );
        const totalCostSummary =
          purchaseCost + insuranceAnnualCost + maintenanceAnnualCost;
        const showCostSummary = totalCostSummary > 0;
        const equipmentTypeSelectOptions = ensureCurrentOption(
          equipmentTypeDropdownOptions,
          values.equipmentType
        );
        const manufacturerSelectOptions = ensureCurrentOption(
          manufacturerDropdownOptions,
          values.manufacturer
        );
        const modelSelectOptions = ensureCurrentOption(modelDropdownOptions, values.model);
        const supplierVendorSelectOptions = ensureCurrentOption(
          supplierVendorDropdownOptions,
          values.supplierVendor
        );
        const paymentTypeSelectOptions = ensureCurrentOption(
          paymentTypeOptions,
          values.paymentType
        );
        const depreciationMethodSelectOptions = ensureCurrentOption(
          depreciationMethodOptions,
          values.depreciationMethod
        );
        const insuranceProviderSelectOptions = ensureCurrentOption(
          insuranceProviderOptions,
          values.insuranceProvider
        );

        const linkedVehicleFieldValue = isVehicleSelectionUnlocked
          ? draftLinkedVehicleOption ||
            findOption(linkedVehicleOptions, values.linkedVehicleId) ||
            null
          : currentLinkedVehicleOption ||
            draftLinkedVehicleOption ||
            findOption(linkedVehicleOptions, values.linkedVehicleId) ||
            null;
        const linkedVehicle = findLinkedVehicle(
          allVehicles,
          linkedVehicleFieldValue?.label || linkedVehicleFieldValue?.value || values.linkedVehicleId
        );
        const selectedVehicleApiId = getPositiveNumber(
          linkedVehicleFieldValue?.optionId,
          linkedVehicleFieldValue?.id,
          linkedVehicleFieldValue?.value
        );
        const currentLinkedVehicleApiId = getPositiveNumber(currentLinkedVehicleRecord?.vehicle_id);
        const isVehicleLinkMode =
          !currentLinkedVehicleApiId || isVehicleSelectionUnlocked;
        const linkedVehicleDisplayLabel =
          linkedVehicleFieldValue?.label ||
          linkedVehicleFieldValue?.value ||
          (currentLinkedVehicleApiId ? `Vehicle ${currentLinkedVehicleApiId}` : "");
        const draftAxleConfigurationsById = new Map(
          (values.axleConfigurations || []).map((axle, index) => [
            String(axle?.axleId || `A${index + 1}`),
            axle,
          ])
        );
        const axleFormIndexById = new Map(
          (values.axleConfigurations || []).map((axle, index) => [
            String(axle?.axleId || `A${index + 1}`),
            index,
          ])
        );
        const assignmentVehicleRows =
          canUseEquipmentHistoryApi && equipmentAssignmentHistoryData
            ? mapEquipmentVehicleHistoryRows(assignmentHistoryVehicleRecords, {
                vehicleOptions: linkedVehicleOptions,
              })
            : buildVehicleHistoryData(equipment?.formData, linkedVehicle);
        const assignmentAxleConfigurations =
          canUseEquipmentHistoryApi && equipmentAssignmentHistoryData
            ? assignmentEquipmentAxleConfigurations
            : values.axleConfigurations || [];
        const displayedAxleConfigurations = assignmentAxleConfigurations.length
          ? assignmentAxleConfigurations.map((axle, index) => {
              const draftAxleConfiguration =
                draftAxleConfigurationsById.get(String(axle?.axleId || `A${index + 1}`)) || {};

              return {
                ...axle,
                tyreConfiguration: hasMeaningfulValue(draftAxleConfiguration?.tyreConfiguration)
                  ? String(draftAxleConfiguration.tyreConfiguration)
                  : String(axle?.tyreConfiguration || "single"),
                assignments: mergeNonEmptyValues(
                  axle?.assignments || {},
                  draftAxleConfiguration?.assignments || {}
                ),
              };
            })
          : values.axleConfigurations || [];
        const assignmentTyreRows =
          canUseEquipmentHistoryApi && equipmentAssignmentHistoryData
            ? mapEquipmentTyreHistoryRows(assignmentHistoryTyreRecords, {
                tyreOptions,
              })
            : buildTyreHistoryData({
                formData: equipment?.formData,
                allTyres,
                equipmentId: values.systemId,
                linkedVehicleId: values.linkedVehicleId,
                linkedVehicleRegistration: linkedVehicle?.registrationNumber || "",
              });
        const paginatedVehicleRows = assignmentVehicleRows.slice(
          (vehiclePage - 1) * vehicleLimit,
          vehiclePage * vehicleLimit
        );
        const paginatedTyreRows = assignmentTyreRows.slice(
          (tyrePage - 1) * tyreLimit,
          tyrePage * tyreLimit
        );
        const tyreLinkSelections = buildEquipmentTyreLinkSelections(
          displayedAxleConfigurations,
          tyreOptions
        );
        const selectedTyreIdsByField = new Map();
        tyreLinkSelections.forEach(({ axleId, selections = [] }) => {
          selections.forEach(({ fieldKey, resolvedTyreId }) => {
            if (resolvedTyreId) {
              selectedTyreIdsByField.set(`${axleId}:${fieldKey}`, String(resolvedTyreId));
            }
          });
        });
        const attachmentAxlesPayload = displayedAxleConfigurations
          .map((axle, index) => {
            const axleRecordId = getPositiveNumber(axle?.id);
            if (!axleRecordId || !equipmentApiId) return null;

            const leftTyreId = resolveSelectOptionId(tyreOptions, axle?.assignments?.left, {
              fallbackId: getPositiveNumber(axle?.assignments?.left),
              initialValue: axle?.assignments?.left,
            });
            const rightTyreId = resolveSelectOptionId(tyreOptions, axle?.assignments?.right, {
              fallbackId: getPositiveNumber(axle?.assignments?.right),
              initialValue: axle?.assignments?.right,
            });
            const leftInnerTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.leftInner,
              {
                fallbackId: getPositiveNumber(axle?.assignments?.leftInner),
                initialValue: axle?.assignments?.leftInner,
              }
            );
            const leftOuterTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.leftOuter,
              {
                fallbackId: getPositiveNumber(
                  axle?.assignments?.leftOuter,
                  axle?.assignments?.left
                ),
                initialValue: axle?.assignments?.leftOuter || axle?.assignments?.left,
              }
            );
            const rightInnerTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.rightInner,
              {
                fallbackId: getPositiveNumber(axle?.assignments?.rightInner),
                initialValue: axle?.assignments?.rightInner,
              }
            );
            const rightOuterTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.rightOuter,
              {
                fallbackId: getPositiveNumber(
                  axle?.assignments?.rightOuter,
                  axle?.assignments?.right
                ),
                initialValue: axle?.assignments?.rightOuter || axle?.assignments?.right,
              }
            );
            const isDoubleTyreConfiguration = axle?.tyreConfiguration === "dual";

            return {
              id: String(axleRecordId),
              outer_left_tyre_id: isDoubleTyreConfiguration
                ? (leftOuterTyreId ? String(leftOuterTyreId) : null)
                : (leftTyreId ? String(leftTyreId) : null),
              outer_right_tyre_id: isDoubleTyreConfiguration
                ? (rightOuterTyreId ? String(rightOuterTyreId) : null)
                : (rightTyreId ? String(rightTyreId) : null),
              inner_left_tyre_id: isDoubleTyreConfiguration
                ? (leftInnerTyreId ? String(leftInnerTyreId) : null)
                : null,
              inner_right_tyre_id: isDoubleTyreConfiguration
                ? (rightInnerTyreId ? String(rightInnerTyreId) : null)
                : null,
              source_id: equipmentApiId,
              source_type: "equipment",
              type: isDoubleTyreConfiguration ? "double" : "single",
            };
          })
          .filter(Boolean);
        const equipmentTyreOdmValue = normalizeNumber(
          equipment?.usageMeterReadingValue ??
            equipmentDetailData?.usage_meter_reading ??
            equipmentDetailData?.meter_reading ??
            equipmentDetailData?.odm,
          0
        );
        let tyreLinkValidationMessage = "";
        if (
          !equipmentApiId ||
          !tyreLinkSelections.length ||
          attachmentAxlesPayload.length !== tyreLinkSelections.length
        ) {
          tyreLinkValidationMessage = "Equipment axle data is missing";
        } else if (!linkedEquipmentTyresSchema.isValidSync(tyreLinkSelections)) {
          tyreLinkValidationMessage = "Please select tyres for all axle positions";
        }
        const canLinkTyres = canUpdate && !tyreLinkValidationMessage;

        const assetHistoryItems = [...activities, ...comments].sort(
          (a, b) => normalizeNumber(b?.createdAtTs, 0) - normalizeNumber(a?.createdAtTs, 0)
        );

        const statusSelectStyles = {
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: 20,
            border: "none",
            borderColor: "transparent",
            boxShadow: "none",
            background: "transparent",
            "&:hover": {
              border: "none",
              borderColor: "transparent",
            },
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            padding: 0,
            paddingLeft: 0,
          }),
          singleValue: (baseStyles) => ({
            ...baseStyles,
            color: currentStatusMeta.color,
            fontSize: "14px",
            fontWeight: 600,
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            color: "#3d3f5c",
            fontSize: "14px",
          }),
          dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            color: "#3d3f5c",
            padding: 0,
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            left: "-28px",
            minWidth: "calc(100% + 28px)",
            width: "max-content",
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            padding: "10px 12px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }),
        };

        const syncAxleCount = (nextValue) => {
          const rawValue = String(nextValue || "");
          setFieldValue("numberOfAxles", rawValue);

          const normalizedCount = rawValue.trim()
            ? Math.max(1, normalizeNumber(rawValue, 3))
            : 3;

          setFieldValue(
            "axleConfigurations",
            buildEquipmentAxleConfigurations(values.axleConfigurations, normalizedCount)
          );
        };

        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function" ? updater(values.attachments || []) : updater;
          setFieldValue("attachments", nextFiles);
        };

        const getSwapAssetOptions = (assetType) => {
          if (assetType === "vehicle") return linkedVehicleOptions;
          if (assetType === "equipment") return equipmentSwapAssetOptions;
          return [];
        };

        const sourceAssetLabel =
          values.systemId ||
          equipment?.systemId ||
          (swapTyreForm.sourceAssetId ? `Equipment ${swapTyreForm.sourceAssetId}` : "");
        const sourceAssetOptions = swapTyreForm.sourceAssetId
          ? [
              {
                label: sourceAssetLabel || String(swapTyreForm.sourceAssetId),
                value: String(swapTyreForm.sourceAssetId),
                optionId: getPositiveNumber(swapTyreForm.sourceAssetId),
              },
            ]
          : getSwapAssetOptions(swapTyreForm.sourceType);
        const targetAssetOptions = getSwapAssetOptions(swapTyreForm.targetType);
        const sourcePositionOptions = sourceAssetTyreOptions;
        const targetPositionOptions = targetAssetTyreOptions;
        const selectedSourcePositionOption = findOption(
          sourcePositionOptions,
          swapTyreForm.sourcePosition
        );
        const selectedTargetPositionOption = findOption(
          targetPositionOptions,
          swapTyreForm.targetPosition
        );

        const resolveSwapAssetOdm = (assetType, assetId) => {
          const normalizedAssetType = normalizeSwapAssetType(assetType);
          const assetOptions =
            normalizedAssetType === "vehicle" ? linkedVehicleOptions : equipmentSwapAssetOptions;
          const matchedOption = findOption(assetOptions, assetId);

          return normalizeNumber(
            matchedOption?.odm ??
              matchedOption?.current_info?.odm ??
              matchedOption?.vehicle?.odm ??
              matchedOption?.usage_meter_reading ??
              matchedOption?.meter_reading ??
              matchedOption?.currentOdometerValue ??
              equipmentTyreOdmValue,
            equipmentTyreOdmValue
          );
        };

        const buildSwapTyrePayloadSection = (sectionKey, selectedOption) => {
          const assetType = normalizeSwapAssetType(swapTyreForm[`${sectionKey}Type`]);
          const assetId = getPositiveNumber(swapTyreForm[`${sectionKey}AssetId`]);

          return {
            position_identifier: String(selectedOption?.positionIdentifier || "").trim(),
            installed_history_id: getPositiveNumber(
              selectedOption?.assignmentId,
              selectedOption?.optionId,
              selectedOption?.id
            ),
            tyre_id: getPositiveNumber(selectedOption?.tyreId),
            axle_id: getPositiveNumber(selectedOption?.axleId),
            odm: resolveSwapAssetOdm(assetType, assetId),
            asset_id: assetId,
            asset_type: assetType,
          };
        };

        const openSwapTyresModal = () => {
          if (!canUpdate) return;

          const sourceAssetId = String(getPositiveNumber(equipmentApiId) || "");

          setSwapTyreForm({
            sourceType: "equipment",
            sourceAssetId,
            sourcePosition: "",
            targetType: "",
            targetAssetId: "",
            targetPosition: "",
          });
          setShowSwapTyresModal(true);
        };

        const handleSwapTypeChange = (sectionKey, nextType) => {
          setSwapTyreForm((previous) => ({
            ...previous,
            [`${sectionKey}Type`]: nextType,
            [`${sectionKey}AssetId`]: "",
            [`${sectionKey}Position`]: "",
          }));
        };

        const handleSwapAssetChange = (sectionKey, assetId) => {
          setSwapTyreForm((previous) => ({
            ...previous,
            [`${sectionKey}AssetId`]: assetId,
            [`${sectionKey}Position`]: "",
          }));
        };

        const handleSwapPositionChange = (sectionKey, position) => {
          setSwapTyreForm((previous) => ({
            ...previous,
            [`${sectionKey}Position`]: position,
          }));
        };

        const handleSwapTyresUpdate = async () => {
          const fromPayload = buildSwapTyrePayloadSection("source", selectedSourcePositionOption);
          const toPayload = buildSwapTyrePayloadSection("target", selectedTargetPositionOption);

          const hasInvalidPayload = [fromPayload, toPayload].some(
            (section) =>
              !section?.position_identifier ||
              !section?.installed_history_id ||
              !section?.tyre_id ||
              !section?.axle_id ||
              !section?.asset_id ||
              !section?.asset_type
          );

          if (hasInvalidPayload) {
            errorNotify("Please select valid tyre positions before updating");
            return;
          }

          setSwapTyresLoading(true);

          try {
            await apiService.swipeTyres({
              from: fromPayload,
              to: toPayload,
            });

            if (equipmentApiId) {
              await dispatch(fetchEquipmentAssignmentHistory(equipmentApiId)).unwrap();
            }
            if (detailEquipmentId) {
              await dispatch(fetchEquipmentDetail(detailEquipmentId)).unwrap();
            }

            setShowSwapTyresModal(false);
            successNotify("Tyres swapped successfully");
          } catch (error) {
            errorNotify(error || "Failed to swap tyres");
          } finally {
            setSwapTyresLoading(false);
          }
        };

        const handleVehicleLinkAction = async () => {
          const vehicleIdForPayload = isVehicleLinkMode
            ? selectedVehicleApiId
            : currentLinkedVehicleApiId;

          if (!vehicleIdForPayload || !equipmentApiId) {
            errorNotify(
              isVehicleLinkMode
                ? "Please select vehicle to link"
                : "Linked vehicle is missing"
            );
            return;
          }

          try {
            const vehiclePayload = {
              vehicle_id: String(vehicleIdForPayload),
              equipment_id: String(equipmentApiId),
              history_id: String(currentLinkedVehicleRecord?.id || ""),
            };

            await dispatch(
              isVehicleLinkMode
                ? attachVehicleEquipment(vehiclePayload)
                : detachVehicleEquipment(vehiclePayload)
            ).unwrap();

            await dispatch(fetchEquipmentAssignmentHistory(equipmentApiId)).unwrap();
            if (isVehicleLinkMode) {
              setFieldValue("linkedVehicleId", linkedVehicleFieldValue?.value || "");
              setLinkedVehicleDraft(linkedVehicleFieldValue?.value || "");
              setIsVehicleSelectionUnlocked(false);
              successNotify("Vehicle linked successfully");
            } else {
              setLinkedVehicleDraft("");
              setFieldValue("linkedVehicleId", "");
              setIsVehicleSelectionUnlocked(true);
              successNotify("Vehicle unlinked successfully");
            }
          } catch (error) {
            errorNotify(
              error ||
                (isVehicleLinkMode
                  ? "Failed to link vehicle"
                  : "Failed to unlink vehicle")
            );
          }
        };

        const handleAttachEquipmentTyres = async () => {
          if (!equipmentApiId || !attachmentAxlesPayload.length) {
            errorNotify("Equipment axle data is missing");
            return;
          }

          if (!canLinkTyres) {
            errorNotify(
              tyreLinkValidationMessage || "Please select tyres for all axle positions"
            );
            return;
          }

          try {
            await dispatch(
              attachEquipmentTyres({
                source_id: equipmentApiId,
                source_type: "equipment",
                axles: attachmentAxlesPayload,
              })
            ).unwrap();

            await dispatch(fetchEquipmentAssignmentHistory(equipmentApiId)).unwrap();
            successNotify("Equipment tyres linked successfully");
          } catch (error) {
            errorNotify(error || "Failed to link equipment tyres");
          }
        };

        const fieldPositionIdentifierMap = {
          left: "outer_left_tyre_id",
          right: "outer_right_tyre_id",
          leftInner: "inner_left_tyre_id",
          leftOuter: "outer_left_tyre_id",
          rightInner: "inner_right_tyre_id",
          rightOuter: "outer_right_tyre_id",
        };

        const handleDetachEquipmentTyre = async ({
          axle,
          axleFormIndex,
          fieldKey,
          fieldLabel,
        }) => {
          const positionIdentifier = String(
            fieldPositionIdentifierMap[fieldKey] || ""
          ).trim();
          const tyreId = resolveSelectOptionId(
            tyreOptions,
            axle?.assignments?.[fieldKey],
            {
              fallbackId: getPositiveNumber(axle?.assignments?.[fieldKey]),
              initialValue: axle?.assignments?.[fieldKey],
            }
          );
          const axleId = getPositiveNumber(axle?.id);
          const sourceId = getPositiveNumber(axle?.sourceId, axle?.source_id, equipmentApiId);
          const sourceType = String(
            axle?.sourceType || axle?.source_type || "equipment"
          ).trim();
          const fieldStateKey = `${axle?.axleId || axleFormIndex}:${fieldKey}`;

          if (!positionIdentifier || !tyreId || !axleId || !sourceId || !sourceType) {
            errorNotify(`Unable to detach tyre from ${fieldLabel}`);
            return;
          }

          setDetachingTyreFieldKey(fieldStateKey);

          try {
            await dispatch(
              detachVehicleTyre({
                axle_id: axleId,
                tyre_id: tyreId,
                position_identifier: positionIdentifier,
                source_id: sourceId,
                source_type: sourceType,
              })
            ).unwrap();

            setFieldValue(
              `axleConfigurations[${axleFormIndex}].assignments.${fieldKey}`,
              ""
            );

            const refreshRequests = [dispatch(fetchTyresDropdown()).unwrap()];

            if (equipmentApiId) {
              refreshRequests.push(
                dispatch(fetchEquipmentAssignmentHistory(equipmentApiId)).unwrap()
              );
            }

            if (detailEquipmentId) {
              refreshRequests.push(dispatch(fetchEquipmentDetail(detailEquipmentId)).unwrap());
            }

            await Promise.all(refreshRequests);
            successNotify(`${fieldLabel} tyre detached successfully`);
          } catch (error) {
            errorNotify(error || `Failed to detach tyre from ${fieldLabel}`);
          } finally {
            setDetachingTyreFieldKey("");
          }
        };

        const canSubmitSwapTyres =
          canUpdate &&
          Boolean(swapTyreForm.sourceAssetId) &&
          Boolean(swapTyreForm.sourcePosition) &&
          Boolean(swapTyreForm.targetAssetId) &&
          Boolean(swapTyreForm.targetPosition);
        const nextInstallmentNumber = String(
          installmentData.reduce(
            (maxValue, row) => Math.max(maxValue, normalizeNumber(row?.number, 0)),
            0
          ) + 1
        ).padStart(2, "0");
        const requiresBankTransferDetails =
          installmentForm.paymentMethod === "bank_transfer";
        const isInstallmentCreationDisabled = isFullPaymentType(values.paymentType);
        const canAddInstallment =
          canUpdate &&
          Boolean(installmentForm.paymentMethod) &&
          Boolean(String(installmentForm.amount || "").trim()) &&
          Boolean(installmentForm.submissionDate) &&
          (!requiresBankTransferDetails ||
            (Boolean(String(installmentForm.bankName || "").trim()) &&
              Boolean(String(installmentForm.accountTitle || "").trim()) &&
              Boolean(String(installmentForm.ibanNumber || "").trim())));
        const selectedInstallmentPaymentMethodLabel =
          paymentMethodOptions.find(
            (option) => String(option?.value || "").trim() === installmentForm.paymentMethod
          )?.label || installmentForm.paymentMethod;

        const handleAddInstallment = async () => {
          if (isInstallmentCreationDisabled) return;

          const nextRow = {
            number: nextInstallmentNumber,
            paymentMethod: selectedInstallmentPaymentMethodLabel,
            amount: normalizeNumber(installmentForm.amount),
            submissionDate: installmentForm.submissionDate,
            dueDate: installmentForm.submissionDate,
            attachmentsCount: (installmentForm.files || []).length,
            attachments: installmentForm.files || [],
            status: "pending",
          };

          if (!canUseEquipmentInstallmentsApi || !equipmentApiId) {
            setLocalInstallmentData((previous) => [...previous, nextRow]);
            setShowAddInstallmentModal(false);
            setInstallmentForm(createInstallmentFormState());
            successNotify("Installment added successfully");
            return;
          }

          try {
            await dispatch(
              createEquipmentInstallment({
                equipment_id: equipmentApiId,
                order_number: parseInt(nextInstallmentNumber, 10) || 1,
                payment_method: selectedInstallmentPaymentMethodLabel,
                amount: normalizeNumber(installmentForm.amount).toFixed(2),
                submit_at: toInstallmentDateTimeString(installmentForm.submissionDate),
                notes: String(installmentForm.notes || "").trim(),
                account_title: installmentForm.accountTitle || "",
                bank_name: installmentForm.bankName || "",
                iban_number: installmentForm.ibanNumber || "",
                reference_number: installmentForm.referenceNumber || "",
                files: normalizeInstallmentFiles(installmentForm.files),
              })
            ).unwrap();

            await dispatch(fetchEquipmentInstallments(equipmentApiId)).unwrap();

            setShowAddInstallmentModal(false);
            setInstallmentForm(createInstallmentFormState());
            successNotify("Installment added successfully");
          } catch (error) {
            errorNotify(error || "Failed to add installment");
          }
        };

        const renderAssignmentAxleSection = (axle, index) => {
          const axleId = axle?.axleId || `A${index + 1}`;
          const axleFormIndex = axleFormIndexById.get(String(axleId)) ?? index;
          const assignmentFields = getEquipmentAxleAssignmentFields(axle?.tyreConfiguration);
          const getAvailableTyreOptions = (fieldKey) => {
            const currentFieldMapKey = `${axleId}:${fieldKey}`;
            const currentSelectedTyreId = selectedTyreIdsByField.get(currentFieldMapKey) || "";
            const blockedTyreIds = new Set(
              Array.from(selectedTyreIdsByField.entries())
                .filter(([mapKey]) => mapKey !== currentFieldMapKey)
                .map(([, tyreId]) => String(tyreId))
            );

            const filteredOptions = tyreOptions.filter((option) => {
              const optionId = getPositiveNumber(option?.optionId, option?.id, option?.value);
              if (!optionId) return true;
              if (currentSelectedTyreId && String(optionId) === currentSelectedTyreId) {
                return true;
              }
              return !blockedTyreIds.has(String(optionId));
            });

            return ensureCurrentOption(filteredOptions, axle?.assignments?.[fieldKey]);
          };

          return (
            <div className="assets_equipment_assignment_group" key={axleId}>
              <h5>Axle: {axleId}</h5>

              <div className="assets_equipment_assignment_configuration">
                <Select
                  label={renderFieldLabel("Tyre Configuration")}
                  placeholder="Select Tyre Configuration"
                  options={tyreConfigurationOptions}
                  value={
                    tyreConfigurationOptions.find(
                      (option) => option.value === axle?.tyreConfiguration
                    ) || null
                  }
                  onChange={(option) =>
                    setFieldValue(
                      `axleConfigurations[${axleFormIndex}].tyreConfiguration`,
                      option?.value || "single"
                    )
                  }
                  onBlur={handleBlur}
                  isSearch={false}
                />
              </div>

              <Row className="assets_equipment_assignment_fields">
                {assignmentFields.map((field) => {
                  const availableTyreOptions = getAvailableTyreOptions(field.key);
                  const fieldLabel = field.label(axleId);
                  const selectedTyreValue = axle?.assignments?.[field.key];
                  const selectedTyreId = resolveSelectOptionId(
                    availableTyreOptions,
                    selectedTyreValue,
                    {
                      fallbackId: getPositiveNumber(selectedTyreValue),
                      initialValue: selectedTyreValue,
                    }
                  );
                  const hasAssignedTyre = Boolean(getPositiveNumber(selectedTyreId));
                  const currentDetachKey = `${axleId}:${field.key}`;
                  const isDetachingCurrentTyre =
                    detachVehicleTyreLoading && detachingTyreFieldKey === currentDetachKey;
                  const isLinkedTyreFromAssignmentHistory =
                    linkedTyreAssignmentFields.has(currentDetachKey);

                  return (
                    <Col md={6} key={`${axleId}-${field.key}`}>
                      <div className="assets_vehicle_tyre_assignment_field">
                        <div className="assets_vehicle_tyre_assignment_select">
                          <Select
                            label={renderFieldLabel(fieldLabel)}
                            placeholder="Select Tyre ID"
                            options={availableTyreOptions}
                            loading={tyreDropdownLoading}
                            value={findOption(availableTyreOptions, selectedTyreValue) || null}
                            onChange={(option) =>
                              setFieldValue(
                                `axleConfigurations[${axleFormIndex}].assignments.${field.key}`,
                                option?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            isSearch={false}
                            isDisabled={isLinkedTyreFromAssignmentHistory}
                          />
                        </div>

                        <button
                          type="button"
                          className="assets_vehicle_tyre_detach_btn"
                          disabled={!canUpdate || !hasAssignedTyre || detachVehicleTyreLoading}
                          onClick={() =>
                            handleDetachEquipmentTyre({
                              axle,
                              axleFormIndex,
                              fieldKey: field.key,
                              fieldLabel,
                            })
                          }
                        >
                          {isDetachingCurrentTyre ? "Detaching..." : "Detach"}
                        </button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        };
        const canSaveActiveTab =
          activeTab === "general_info" || activeTab === "financial_insurance";
        const isSaveDetailsLoading = isSubmitting || updateEquipmentPurchaseInfoLoading;
        const isSaveDetailsDisabled = isSaveDetailsLoading || !canSaveActiveTab;

        return (
          <div className="assets_vehicle_details_page">
            <Breadcrumbs values={["Assets", "Equipments", "Equipment Details"]} />

            <div className="page_header access_view">
              <h2>Equipment Details</h2>
              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  submitBtn={handleSubmit}
                  submitText="Save Details"
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={canUpdate}
                  loading={isSaveDetailsLoading}
                  disabled={isSaveDetailsDisabled}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={8}>
                  <div className="assets_vehicle_header_strip">
                    <h6>
                      Equipment ID: <span>{values.systemId}</span>
                    </h6>

                    <div className="assets_vehicle_top_status">
                      <span>Status</span>
                      <Select
                        className="assets_vehicle_status_select"
                        label=""
                        placeholder="Select Status"
                        options={equipmentStatusTopOptions}
                        value={findOption(equipmentStatusTopOptions, values.status)}
                        onChange={(option) => setFieldValue("status", option?.value || "")}
                        onBlur={handleBlur}
                        isSearch={false}
                        showIcon
                        icon={
                          <img
                            src={currentStatusMeta.icon}
                            alt={currentStatusMeta.label}
                            className="assets_vehicle_status_icon_inline"
                          />
                        }
                        customStyles={statusSelectStyles}
                        required={false}
                      />
                    </div>
                  </div>

                  <div className="assets_vehicle_tabs_wrap">
                    <CustomTabs tabs={tabOptions} activeTab={activeTab} setActiveTab={setActiveTab} />
                  </div>

                  {activeTab === "general_info" && (
                    <div className="assets_equipment_general_info">
                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>General Details</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Asset Number")}
                              placeholder="Enter Asset No."
                              name="assetNumber"
                              value={values.assetNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.assetNumber}
                              touched={touched.assetNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Equipment Type")}
                              placeholder="Select Equipment Type"
                              options={equipmentTypeSelectOptions}
                              value={findOptionByOptionIdOrValue(
                                equipmentTypeSelectOptions,
                                values.equipmentTypeId,
                                values.equipmentType
                              )}
                              onChange={(option) => {
                                setFieldValue("equipmentType", option?.value || "");
                                setFieldValue(
                                  "equipmentTypeId",
                                  option?.optionId ?? option?.id ?? ""
                                );
                              }}
                              onBlur={handleBlur}
                              error={errors.equipmentType}
                              touched={touched.equipmentType}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Serial / Chassis No.")}
                              placeholder="Enter Serial / Chassis No."
                              name="serialNumber"
                              value={values.serialNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.serialNumber}
                              touched={touched.serialNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Registration Number")}
                              placeholder="Enter Asset No."
                              name="registrationNumber"
                              value={values.registrationNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.registrationNumber}
                              touched={touched.registrationNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Status")}
                              placeholder="Select Status"
                              options={equipmentStatusSelectOptions}
                              value={findOption(equipmentStatusSelectOptions, values.status)}
                              onChange={(option) => setFieldValue("status", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.status}
                              touched={touched.status}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={12} style={{ padding: 0 }}>
                            <Input
                              label={renderFieldLabel("Description", false)}
                              placeholder="Write detail of part if any..."
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.description}
                              touched={touched.description}
                              quill
                              required={false}
                              setFieldValue={setFieldValue}
                              className="create_vehicle_description"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Manufacturer Details</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Manufacturer")}
                              placeholder="Select Manufacturer"
                              options={manufacturerSelectOptions}
                              value={findOptionByOptionIdOrValue(
                                manufacturerSelectOptions,
                                values.manufacturerId,
                                values.manufacturer
                              )}
                              onChange={(option) => {
                                setFieldValue("manufacturer", option?.value || "");
                                setFieldValue(
                                  "manufacturerId",
                                  option?.optionId ?? option?.id ?? ""
                                );
                              }}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Model")}
                              placeholder="Select Model"
                              options={modelSelectOptions}
                              value={findOptionByOptionIdOrValue(
                                modelSelectOptions,
                                values.modelId,
                                values.model
                              )}
                              onChange={(option) => {
                                setFieldValue("model", option?.value || "");
                                setFieldValue("modelId", option?.optionId ?? option?.id ?? "");
                              }}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Year of Manufacture")}
                              placeholder="YYYY"
                              name="yearOfManufacture"
                              type="number"
                              value={values.yearOfManufacture}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showIcon={<FiCalendar />}
                              className="assets_equipment_year_input"
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Asset Number")}
                              placeholder="Select Country"
                              options={countryOptions}
                              value={findOption(countryOptions, values.countryOfOrigin)}
                              onChange={(option) =>
                                setFieldValue("countryOfOrigin", option?.value || "")
                              }
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Supplier / Vendor")}
                              placeholder="Select Option"
                              options={supplierVendorSelectOptions}
                              value={findOptionByOptionIdOrValue(
                                supplierVendorSelectOptions,
                                values.supplierVendorId,
                                values.supplierVendor
                              )}
                              onChange={(option) => {
                                setFieldValue("supplierVendor", option?.value || "");
                                setFieldValue(
                                  "supplierVendorId",
                                  option?.optionId ?? option?.id ?? ""
                                );
                              }}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Technical Specification</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Number of Axle")}
                              placeholder="00"
                              name="numberOfAxles"
                              type="number"
                              value={values.numberOfAxles}
                              onChange={(event) => syncAxleCount(event?.target?.value)}
                              onBlur={handleBlur}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Break System")}
                              placeholder="Select Break System"
                              options={brakeSystemOptions}
                              value={findOption(brakeSystemOptions, values.brakeSystem)}
                              onChange={(option) =>
                                setFieldValue("brakeSystem", option?.value || "")
                              }
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Number of Tyres")}
                              placeholder="00"
                              name="numberOfTyres"
                              type="number"
                              value={values.numberOfTyres}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Suspension Type")}
                              placeholder="Select Type"
                              options={suspensionTypeOptions}
                              value={findOption(suspensionTypeOptions, values.suspensionType)}
                              onChange={(option) =>
                                setFieldValue("suspensionType", option?.value || "")
                              }
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Capacity Information</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Tanker / Bowser Capacity")}
                              placeholder="00"
                              name="capacityValue"
                              type="number"
                              value={values.capacityValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText={values.capacityUnit || "Liters"}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Number of Compartments")}
                              placeholder="00"
                              name="numberOfCompartments"
                              type="number"
                              value={values.numberOfCompartments}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="Liters"
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Container Size")}
                              placeholder="Select Size"
                              options={containerSizeOptions}
                              value={findOption(containerSizeOptions, values.containerSize)}
                              onChange={(option) =>
                                setFieldValue("containerSize", option?.value || "")
                              }
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Load Capacity")}
                              placeholder="00"
                              name="loadCapacity"
                              type="number"
                              value={values.loadCapacity}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="Kg / Tons"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Dimensional Data</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Length")}
                              placeholder="00"
                              name="length"
                              type="number"
                              value={values.length}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="mm / Meters"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("width")}
                              placeholder="00"
                              name="width"
                              type="number"
                              value={values.width}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="mm / Meters"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Height")}
                              placeholder="00"
                              name="height"
                              type="number"
                              value={values.height}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="mm / Meters"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Tare Weight")}
                              placeholder="00"
                              name="tareWeight"
                              type="number"
                              value={values.tareWeight}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="Kg"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Gross Weight")}
                              placeholder="00"
                              name="grossWeight"
                              type="number"
                              value={values.grossWeight}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                              showExtraText
                              extraText="Kg (tons)"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_vehicle_documents_section">
                        <h6>Photos &amp; Documents</h6>
                        <p>Upload relevant photos or documents for this equipment.</p>
                        <FileUpload
                          destination="equipment-registration"
                          files={values.attachments || []}
                          setFiles={updateAttachments}
                          maxFileSize={10}
                          maxFiles={5}
                          className="create_vehicle_upload assets_vehicle_documents_upload"
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === "service_records" && (
                    <div className="content_box assets_vehicle_content_box">
                      <h6>Equipment Record</h6>
                      <p>Main information for this part record.</p>

                      <NewTableComponent
                        showAddBtn={false}
                        showFilter={false}
                        columns={serviceColumns}
                        data={paginatedServiceRows}
                        currentPage={servicePage}
                        setCurrentPage={setServicePage}
                        limit={serviceLimit}
                        setLimit={(value) => {
                          setServicePage(1);
                          setServiceLimit(value);
                        }}
                        totalItems={serviceTotalItems}
                        totalPages={serviceTotalPages}
                        showPagination
                        paginationLoading={canUseEquipmentServicesApi && equipmentServiceRecordsLoading}
                        loading={canUseEquipmentServicesApi && equipmentServiceRecordsLoading}
                        searchFilters={{}}
                        setSearchFilters={() => { }}
                        tableClassName="assets_vehicle_inner_table"
                        limitOptions={[10, 20, 50, 100]}
                        paginationClassName="inline_pagination"
                      />
                    </div>
                  )}

                  {activeTab === "assignment_history" && (
                    <>
                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Vehicle Record</h6>
                        <p>Main information for this part record.</p>

                        <div className="assets_vehicle_assignment_link_row">
                          <div className="assets_vehicle_assignment_link_field">
                            {isVehicleLinkMode ? (
                              <AssetLinkField
                                className="assets_equipment_assignment_link_select"
                                label="Current Linked Vehicle"
                                placeholder="Select Vehicle"
                                options={linkedVehicleOptions}
                                loading={dropdownLoading || linkedVehicleDropdownLoading}
                                value={linkedVehicleFieldValue}
                                onChange={(option) => {
                                  setLinkedVehicleDraft(option?.value || "");
                                  setFieldValue("linkedVehicleId", option?.value || "");
                                }}
                                onBlur={() => { }}
                                isSearch
                                isDisabled={
                                  !isVehicleSelectionUnlocked || attachVehicleEquipmentLoading
                                }
                              />
                            ) : (
                              <div className="assets_vehicle_assignment_link_display">
                                <span
                                  className="service-task"
                                  title={linkedVehicleDisplayLabel}
                                >
                                  {linkedVehicleDisplayLabel}
                                </span>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="assets_vehicle_assignment_unlink"
                            onClick={handleVehicleLinkAction}
                            disabled={
                              !canUpdate ||
                              attachVehicleEquipmentLoading ||
                              (isVehicleLinkMode && !selectedVehicleApiId)
                            }
                          >
                            {linkedIcon}
                            <span>
                              {attachVehicleEquipmentLoading
                                ? isVehicleLinkMode
                                  ? "Linking..."
                                  : "Unlinking..."
                                : isVehicleLinkMode
                                  ? "Link"
                                  : "Unlink"}
                            </span>
                          </button>
                        </div>

                        <h5 className="assets_equipment_assignment_history_title">
                          Vehicle Assignment History
                        </h5>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={vehicleColumns}
                          data={paginatedVehicleRows}
                          currentPage={vehiclePage}
                          setCurrentPage={setVehiclePage}
                          limit={vehicleLimit}
                          setLimit={(value) => {
                            setVehiclePage(1);
                            setVehicleLimit(value);
                          }}
                          totalItems={assignmentVehicleRows.length}
                          totalPages={Math.max(1, Math.ceil(assignmentVehicleRows.length / vehicleLimit))}
                          showPagination={assignmentVehicleRows.length > vehicleLimit}
                          paginationLoading={canUseEquipmentHistoryApi && equipmentAssignmentHistoryLoading}
                          loading={canUseEquipmentHistoryApi && equipmentAssignmentHistoryLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table assets_equipment_assignment_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <div className="assets_equipment_assignment_header">
                          <div>
                            <h6>Linked Tyres &amp; Positions</h6>
                            <p>Main information for this part record.</p>
                          </div>

                          <button
                            type="button"
                            className="assets_equipment_swap_btn"
                            onClick={openSwapTyresModal}
                            disabled={!canUpdate}
                          >
                            <FiRefreshCw />
                            Swap Tyres
                          </button>
                        </div>

                        <div className="assets_equipment_assignment_wrap">
                          {displayedAxleConfigurations.map((axle, index) =>
                            renderAssignmentAxleSection(axle, index)
                          )}
                        </div>

                        <div className="assets_vehicle_link_tyres_actions">
                          <button
                            type="button"
                            className="main_btn assets_vehicle_link_tyres_btn"
                            onClick={handleAttachEquipmentTyres}
                            disabled={!canLinkTyres || attachEquipmentTyresLoading}
                          >
                            {attachEquipmentTyresLoading ? "Linking..." : "Link Tyre"}
                          </button>
                        </div>

                        <h5 className="assets_equipment_assignment_history_title">
                          Tyres Installed History
                        </h5>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={tyreColumns}
                          data={paginatedTyreRows}
                          currentPage={tyrePage}
                          setCurrentPage={setTyrePage}
                          limit={tyreLimit}
                          setLimit={(value) => {
                            setTyrePage(1);
                            setTyreLimit(value);
                          }}
                          totalItems={assignmentTyreRows.length}
                          totalPages={Math.max(1, Math.ceil(assignmentTyreRows.length / tyreLimit))}
                          showPagination={assignmentTyreRows.length > tyreLimit}
                          paginationLoading={canUseEquipmentHistoryApi && equipmentAssignmentHistoryLoading}
                          loading={canUseEquipmentHistoryApi && equipmentAssignmentHistoryLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table assets_equipment_assignment_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>
                    </>
                  )}

                  {activeTab === "financial_insurance" && (
                    <>
                      <div className="content_box assets_vehicle_content_box">
                        <h6>Purchase Summary</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Purchase Date")}
                              type="date"
                              placeholder="DD/MM/YYYY"
                              name="purchaseDate"
                              value={values.purchaseDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.purchaseDate}
                              touched={touched.purchaseDate}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Purchase Cost")}
                              type="number"
                              placeholder="00"
                              name="purchaseCost"
                              value={values.purchaseCost}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.purchaseCost}
                              touched={touched.purchaseCost}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Payment Type")}
                              placeholder="Select Payment Type"
                              name="paymentType"
                              options={paymentTypeSelectOptions}
                              value={findOption(paymentTypeSelectOptions, values.paymentType)}
                              isDisabled={isInstallmentCreationDisabled}
                              onChange={(option) => {
                                const nextPaymentType = option?.value || "";
                                setFieldValue("paymentType", nextPaymentType);

                                if (isFullPaymentType(nextPaymentType)) {
                                  setShowAddInstallmentModal(false);
                                  setInstallmentForm(createInstallmentFormState());
                                }
                              }}
                              onBlur={handleBlur}
                              error={errors.paymentType}
                              touched={touched.paymentType}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Depreciation Method")}
                              placeholder="Select Depreciation Method"
                              name="depreciationMethod"
                              options={depreciationMethodSelectOptions}
                              value={findOption(
                                depreciationMethodSelectOptions,
                                values.depreciationMethod
                              )}
                              onChange={(option) =>
                                setFieldValue("depreciationMethod", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.depreciationMethod}
                              touched={touched.depreciationMethod}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Useful Life")}
                              type="number"
                              placeholder="Enter Useful Life"
                              name="usefulLife"
                              value={values.usefulLife}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.usefulLife}
                              touched={touched.usefulLife}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Residual Value")}
                              type="number"
                              placeholder="00"
                              name="residualValue"
                              value={values.residualValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.residualValue}
                              touched={touched.residualValue}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box">
                        <h6>Installment Record</h6>
                        <p>Main information for this part record.</p>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={installmentColumns}
                          data={paginatedInstallmentRows}
                          currentPage={installmentPage}
                          setCurrentPage={setInstallmentPage}
                          limit={installmentLimit}
                          setLimit={(value) => {
                            setInstallmentPage(1);
                            setInstallmentLimit(value);
                          }}
                          totalItems={installmentData.length}
                          totalPages={Math.max(1, Math.ceil(installmentData.length / installmentLimit))}
                          showPagination={false}
                          paginationLoading={false}
                          loading={canUseEquipmentInstallmentsApi && equipmentInstallmentsLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table"
                        />
                        <button
                          type="button"
                          className="asset_vehicle_link_row_btn"
                          onClick={() => {
                            if (isInstallmentCreationDisabled) return;
                            setShowAddInstallmentModal(true);
                          }}
                          disabled={isInstallmentCreationDisabled}
                        >
                          <FiPlus />
                          Add Installment
                        </button>
                      </div>

                      <div className="content_box assets_vehicle_content_box">
                        <h6>Insurance History</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Policy Number")}
                              placeholder="Enter Policy Number"
                              name="policyNumber"
                              value={values.policyNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.policyNumber}
                              touched={touched.policyNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Provider")}
                              placeholder="Select Provider"
                              name="insuranceProvider"
                              options={insuranceProviderSelectOptions}
                              value={findOption(
                                insuranceProviderSelectOptions,
                                values.insuranceProvider
                              )}
                              onChange={(option) =>
                                setFieldValue("insuranceProvider", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.insuranceProvider}
                              touched={touched.insuranceProvider}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Coverage Type")}
                              placeholder="Select Coverage Type"
                              name="coverageType"
                              options={coverageTypeOptions}
                              value={findOption(coverageTypeOptions, values.coverageType)}
                              onChange={(option) =>
                                setFieldValue("coverageType", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.coverageType}
                              touched={touched.coverageType}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Coverage Amount")}
                              placeholder="00"
                              name="coverageAmount"
                              type="number"
                              value={values.coverageAmount}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.coverageAmount}
                              touched={touched.coverageAmount}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Premium")}
                              placeholder="00"
                              name="premiumAmount"
                              type="number"
                              value={values.premiumAmount}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.premiumAmount}
                              touched={touched.premiumAmount}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Start Date")}
                              type="date"
                              placeholder="DD/MM/YYYY"
                              name="insuranceStartDate"
                              value={values.insuranceStartDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.insuranceStartDate}
                              touched={touched.insuranceStartDate}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Expiry Date")}
                              type="date"
                              placeholder="DD/MM/YYYY"
                              name="insuranceExpiryDate"
                              value={values.insuranceExpiryDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.insuranceExpiryDate}
                              touched={touched.insuranceExpiryDate}
                              required={false}
                            />
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}

                  {activeTab === "operational_data" && (
                    <>
                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Trip History</h6>
                        <p>Main information for this part record.</p>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={tripColumns}
                          data={paginatedTripRows}
                          currentPage={tripPage}
                          setCurrentPage={setTripPage}
                          limit={tripLimit}
                          setLimit={(value) => {
                            setTripPage(1);
                            setTripLimit(value);
                          }}
                          totalItems={tripTotalItems}
                          totalPages={tripTotalPages}
                          showPagination
                          paginationLoading={canUseEquipmentTripsApi && equipmentTripsLoading}
                          loading={canUseEquipmentTripsApi && equipmentTripsLoading}
                          searchFilters={{}}
                          setSearchFilters={() => {}}
                          tableClassName="assets_vehicle_inner_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Distance Summary</h6>
                        <p>Main information for this part record.</p>
                        
                        <Row className="g-3 mt-1">
                          <Col md={4}>
                            <div style={{ border: "1px solid #eaebf0", borderRadius: "10px", padding: "30px", textAlign: "center" }}>
                              <span style={{ display: "block", color: "#888ea8", fontSize: "13px", marginBottom: "10px" }}>Total KM</span>
                              <strong style={{ color: "#3d3f5c", fontSize: "22px", fontWeight: "700" }}>
                                {canUseEquipmentDistanceSummaryApi && equipmentDistanceSummaryLoading
                                  ? "Loading..."
                                  : formatAmount(operationalSummary.totalKm)}
                              </strong>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div style={{ border: "1px solid #eaebf0", borderRadius: "10px", padding: "30px", textAlign: "center" }}>
                              <span style={{ display: "block", color: "#888ea8", fontSize: "13px", marginBottom: "10px" }}>Average KM per Month</span>
                              <strong style={{ color: "#3d3f5c", fontSize: "22px", fontWeight: "700" }}>
                                {canUseEquipmentDistanceSummaryApi && equipmentDistanceSummaryLoading
                                  ? "Loading..."
                                  : formatAmount(operationalSummary.averageKmPerMonth)}
                              </strong>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div style={{ border: "1px solid #eaebf0", borderRadius: "10px", padding: "30px", textAlign: "center" }}>
                              <span style={{ display: "block", color: "#888ea8", fontSize: "13px", marginBottom: "10px" }}>Last 30 Days KM</span>
                              <strong style={{ color: "#3d3f5c", fontSize: "22px", fontWeight: "700" }}>
                                {canUseEquipmentDistanceSummaryApi && equipmentDistanceSummaryLoading
                                  ? "Loading..."
                                  : formatAmount(operationalSummary.last30DaysKm)}
                              </strong>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}
                </Col>

                <Col md={4}>
                  <div className="service_cost_summary_details assets_vehicle_right_sidebar">
                    <div className="service_cost_summary mb-3">
                      <h6>Cost Summary</h6>
                      <p>Financial Breakdown of the Service Entries (PKR)</p>

                      {showCostSummary ? (
                        <>
                          <div className="service_item">
                            <div>Purchase Cost</div>
                            <div>{formatAmount(purchaseCost)}</div>
                          </div>
                          <div className="service_item">
                            <div>Insurance Cost (Annual)</div>
                            <div>{formatAmount(insuranceAnnualCost)}</div>
                          </div>
                          <div className="service_item">
                            <div>Maintenance Cost (Annual)</div>
                            <div>{formatAmount(maintenanceAnnualCost)}</div>
                          </div>
                          <div className="total">
                            <div>Total</div>
                            <span>{formatAmount(totalCostSummary)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="assets_vehicle_empty_state">
                          <p>No cost records available to generate summary</p>
                        </div>
                      )}
                    </div>

                    <LogsComments
                      {...(canUseEquipmentHistoryApi
                        ? {
                            historyItems: Array.isArray(equipmentHistoryData)
                              ? equipmentHistoryData
                              : [],
                            onFetchHistory: handleFetchEquipmentHistory,
                            onSubmitComment: handleSubmitEquipmentComment,
                            loading: equipmentHistoryLoading,
                          }
                        : {
                            staticHistoryItems: assetHistoryItems,
                            commentValue: commentInput,
                            onCommentChange: setCommentInput,
                            onSendComment: handleCommentSend,
                          })}
                      editorModules={modules}
                      editorFormats={formats}
                      emptyMessages={{
                        all: "No comments or user activity found",
                        comment: "No comments found",
                        logs: "No user activity found",
                      }}
                    />

                    <div className="service_cost_summary_details rfq_right_card">
                      <div className="parts_general_notes">
                        <h5>General Notes</h5>
                        <p>Add any additional notes or observations here.</p>
                        <ReactQuill
                          placeholder={"write notes here..."}
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          onChange={(val) => setFieldValue("notes", val)}
                          formats={formats}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <CustomModal
              show={attachmentViewer.open}
              handleClose={closeAttachmentViewer}
              heading="Installment Attachments"
              size="lg"
              dialogClassName="custom_add_installment_modal"
            >
              <div className="assets_equipment_swap_modal_body custom_add_installment_modal_body">
                <div
                  style={{
                    position: "relative",
                    minHeight: "420px",
                    border: "1px solid #EAECF0",
                    borderRadius: "12px",
                    background: "#F8FAFC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {hasMultipleAttachments ? (
                    <>
                      <button
                        type="button"
                        onClick={() => changeAttachmentSlide(-1)}
                        aria-label="Previous attachment"
                        style={{
                          position: "absolute",
                          left: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          border: "1px solid #EAECF0",
                          background: "#FFFFFF",
                          color: "#25273D",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 10px 24px rgba(16, 24, 40, 0.16)",
                          zIndex: 2,
                        }}
                      >
                        <FiChevronLeft size={22} />
                      </button>
                      <button
                        type="button"
                        onClick={() => changeAttachmentSlide(1)}
                        aria-label="Next attachment"
                        style={{
                          position: "absolute",
                          right: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          border: "1px solid #EAECF0",
                          background: "#FFFFFF",
                          color: "#25273D",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 10px 24px rgba(16, 24, 40, 0.16)",
                          zIndex: 2,
                        }}
                      >
                        <FiChevronRight size={22} />
                      </button>
                    </>
                  ) : null}
                  <span
                    style={{
                      position: "absolute",
                      right: "14px",
                      bottom: "12px",
                      padding: "5px 10px",
                      borderRadius: "999px",
                      background: "rgba(37, 39, 61, 0.72)",
                      color: "#FFFFFF",
                      fontSize: "12px",
                      zIndex: 2,
                    }}
                  >
                    {attachmentViewer.currentIndex + 1} / {attachmentViewer.urls.length}
                  </span>
                  {isImageAttachment(currentAttachmentUrl) ? (
                    <img
                      src={currentAttachmentUrl}
                      alt={currentAttachmentName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "420px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <iframe
                      src={currentAttachmentUrl}
                      title={currentAttachmentName}
                      style={{
                        width: "100%",
                        height: "420px",
                        border: "none",
                        background: "#fff",
                      }}
                    />
                  )}
                </div>
              </div>
            </CustomModal>

            <CustomModal
              show={showSwapTyresModal}
              handleClose={() => setShowSwapTyresModal(false)}
              heading="Swap Tyres"
              size="lg"
              dialogClassName="assets_equipment_swap_modal"
            >
              <div className="assets_equipment_swap_modal_body">
                <div className="assets_equipment_swap_modal_section">
                  <h5>Swap From</h5>

                  <Row>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel("Select Type")}
                        placeholder="Select Type"
                        options={swapTypeOptions}
                        value={findOption(swapTypeOptions, swapTyreForm.sourceType)}
                        onChange={(option) =>
                          handleSwapTypeChange("source", option?.value || "")
                        }
                        isDisabled
                        isSearch={false}
                      />
                    </Col>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel(getSwapAssetFieldLabel(swapTyreForm.sourceType))}
                        placeholder={`Select ${getSwapAssetFieldLabel(swapTyreForm.sourceType)}`}
                        options={sourceAssetOptions}
                        value={findOption(sourceAssetOptions, swapTyreForm.sourceAssetId)}
                        onChange={(option) =>
                          handleSwapAssetChange(
                            "source",
                            String(option?.optionId || option?.id || option?.value || "")
                          )
                        }
                        isDisabled
                        isSearch={false}
                      />
                    </Col>
                    <Col md={12}>
                      <Select
                        label={renderFieldLabel("Select Position/Tyre")}
                        placeholder="Select Position / Tyre"
                        options={sourcePositionOptions}
                        value={
                          sourcePositionOptions.find(
                            (option) => option.value === swapTyreForm.sourcePosition
                          ) || null
                        }
                        onChange={(option) =>
                          handleSwapPositionChange("source", option?.value || "")
                        }
                        loading={sourceAssetTyresLoading}
                        isSearch={false}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="assets_equipment_swap_modal_section">
                  <h5>Swap To</h5>

                  <Row>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel("Select Type")}
                        placeholder="Select Type"
                        options={swapTypeOptions}
                        value={findOption(swapTypeOptions, swapTyreForm.targetType)}
                        onChange={(option) =>
                          handleSwapTypeChange("target", option?.value || "")
                        }
                        isSearch={false}
                      />
                    </Col>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel(getSwapAssetFieldLabel(swapTyreForm.targetType))}
                        placeholder={`Select ${getSwapAssetFieldLabel(swapTyreForm.targetType)}`}
                        options={targetAssetOptions}
                        value={findOption(targetAssetOptions, swapTyreForm.targetAssetId)}
                        onChange={(option) =>
                          handleSwapAssetChange(
                            "target",
                            String(option?.optionId || option?.id || option?.value || "")
                          )
                        }
                        isDisabled={!swapTyreForm.targetType}
                        isSearch={false}
                      />
                    </Col>
                    <Col md={12}>
                      <Select
                        label={renderFieldLabel("Select Position/Tyre")}
                        placeholder="Select Position / Tyre"
                        options={targetPositionOptions}
                        value={findOption(targetPositionOptions, swapTyreForm.targetPosition)}
                        onChange={(option) =>
                          handleSwapPositionChange("target", option?.value || "")
                        }
                        isDisabled={!swapTyreForm.targetAssetId}
                        loading={targetAssetTyresLoading}
                        isSearch={false}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="assets_equipment_swap_modal_actions">
                  <Button
                    cancelBtn={() => setShowSwapTyresModal(false)}
                    submitBtn={handleSwapTyresUpdate}
                    submitText="Update"
                    loading={swapTyresLoading}
                    type="button"
                    shouldRow={false}
                    disabled={!canSubmitSwapTyres}
                  />
                </div>
              </div>
            </CustomModal>

            <CustomModal
              show={showAddInstallmentModal}
              handleClose={() => {
                setShowAddInstallmentModal(false);
                setInstallmentForm(createInstallmentFormState());
              }}
              heading="Add Installment"
              size="lg"
              dialogClassName="custom_add_installment_modal"
            >
              <div className="assets_equipment_swap_modal_body custom_add_installment_modal_body p-0">
                <div style={{ padding: 0 }}>
                  <Row>
                    <Col md={12}>
                      <div className="assets_add_installment_info_box">
                        <span>Installment:</span> {nextInstallmentNumber}
                      </div>
                    </Col>
                    <Col md={12}>
                      <Select
                        label={renderFieldLabel("Payment Method")}
                        placeholder="Select Payment Method"
                        options={paymentMethodOptions}
                        value={findOption(paymentMethodOptions, installmentForm.paymentMethod)}
                        onChange={(option) =>
                          setInstallmentForm((prev) => {
                            const nextMethod = option?.value || "";
                            if (nextMethod === "bank_transfer") {
                              return {
                                ...prev,
                                paymentMethod: nextMethod,
                              };
                            }

                            return {
                              ...prev,
                              paymentMethod: nextMethod,
                              bankName: "",
                              accountTitle: "",
                              ibanNumber: "",
                              referenceNumber: "",
                            };
                          })
                        }
                        isSearch={false}
                        required={false}
                      />
                    </Col>
                    <Col md={12}>
                      <Input
                        label={renderFieldLabel("Amount")}
                        placeholder="00"
                        type="number"
                        value={installmentForm.amount}
                        onChange={(e) =>
                          setInstallmentForm((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        required={false}
                        showExtraText
                        extraText="PKR"
                      />
                    </Col>
                    <Col md={12}>
                      <Input
                        label={renderFieldLabel("Submission Date")}
                        type="date"
                        placeholder="DD/MM/YYYY"
                        value={installmentForm.submissionDate}
                        onChange={(e) =>
                          setInstallmentForm((prev) => ({
                            ...prev,
                            submissionDate: e.target.value,
                          }))
                        }
                        required={false}
                      />
                    </Col>
                    {installmentForm.paymentMethod === "bank_transfer" && (
                      <>
                        <Col md={12}>
                          <Select
                            label={renderFieldLabel("Bank")}
                            placeholder="Select Bank"
                            options={banksDropdownData || []}
                            value={
                              (banksDropdownData || []).find(
                                (option) =>
                                  String(option?.label || option?.name || "").trim() ===
                                  String(installmentForm.bankName || "").trim()
                              ) || null
                            }
                            onChange={(option) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                bankName: option?.label || option?.name || "",
                              }))
                            }
                            isSearch={false}
                            required={false}
                          />
                        </Col>
                        <Col md={12}>
                          <Input
                            label={renderFieldLabel("Account Title")}
                            type="text"
                            placeholder="Enter Account Title"
                            value={installmentForm.accountTitle}
                            onChange={(e) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                accountTitle: e.target.value,
                              }))
                            }
                            required={false}
                          />
                        </Col>
                        <Col md={12}>
                          <Input
                            label={renderFieldLabel("IBAN Number")}
                            type="text"
                            placeholder="PK00XXXX"
                            value={installmentForm.ibanNumber}
                            onChange={(e) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                ibanNumber: e.target.value,
                              }))
                            }
                            required={false}
                          />
                        </Col>
                        <Col md={12}>
                          <Input
                            label={renderFieldLabel("Reference Number", false)}
                            type="text"
                            placeholder="REF-123456"
                            value={installmentForm.referenceNumber}
                            onChange={(e) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                referenceNumber: e.target.value,
                              }))
                            }
                            required={false}
                          />
                        </Col>
                      </>
                    )}
                    <Col md={12}>
                      <div className="parts_general_notes">
                        <h5 className="mb-2">Notes</h5>
                        <ReactQuill
                          placeholder={"write notes here..."}
                          modules={modules}
                          theme="snow"
                          value={installmentForm.notes || ""}
                          onChange={(value) =>
                            setInstallmentForm((prev) => ({
                              ...prev,
                              notes: value,
                            }))
                          }
                          formats={formats}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </Col>
                    <Col md={12} className="mb-3">
                      <FileUpload
                        destination="installment-files"
                        files={installmentForm.files}
                        setFiles={(nextFiles) =>
                          setInstallmentForm((prev) => ({
                            ...prev,
                            files:
                              typeof nextFiles === "function"
                                ? nextFiles(prev.files || [])
                                : nextFiles,
                          }))
                        }
                        maxFileSize={10}
                        maxFiles={5}
                      />
                    </Col>
                  </Row>
                </div>
                <div className="assets_equipment_swap_modal_actions d-flex gap-2 justify-content-end mt-4">
                  <Button
                    cancelBtn={() => {
                      setShowAddInstallmentModal(false);
                      setInstallmentForm(createInstallmentFormState());
                    }}
                    submitBtn={handleAddInstallment}
                    submitText="Add"
                    type="button"
                    shouldRow={false}
                    loading={canUseEquipmentInstallmentsApi && createEquipmentInstallmentLoading}
                    disabled={!canAddInstallment || isInstallmentCreationDisabled}
                  />
                </div>
              </div>
            </CustomModal>
          </div>
        );
      }}
    </Formik>
  );
};

export default EquipmentDetails;

// ================== EQUIPMENT API MAPPING HELPERS ==================
const toText = (value) => String(value ?? "").trim();

const pickFirstText = (...values) => {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return "";
};

const toComparableNumber = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const numberOrBlank = (value) => {
  const parsed = toComparableNumber(value);
  return parsed === null ? "" : parsed;
};

const formatDisplayNumber = (value) => {
  const numeric = toComparableNumber(value);
  if (numeric === null) return "";
  return numeric.toLocaleString("en-US");
};

const formatDateOnly = (value) => {
  if (!value) return "";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return toText(value);

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDurationBetween = (startValue, endValue, { current = false } = {}) => {
  if (current) return "Current";

  const parsedStart = new Date(startValue);
  const parsedEnd = new Date(endValue);
  if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
    return "-";
  }

  const diffInMs = parsedEnd.getTime() - parsedStart.getTime();
  if (diffInMs <= 0) return "Same day";

  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  return `${diffInDays} day${diffInDays === 1 ? "" : "s"}`;
};

const formatUsageMeterReading = (value, usageType) => {
  const numericValue = toComparableNumber(value) || 0;
  const formattedValue = numericValue.toLocaleString("en-US");
  const normalizedUsageType = toText(usageType).toLowerCase();

  if (normalizedUsageType === "hours") return `${formattedValue} hrs`;
  if (normalizedUsageType === "cycles") return `${formattedValue} cycles`;
  if (normalizedUsageType === "trips") return `${formattedValue} trips`;
  return `${formattedValue} km`;
};

const findOptionByValue = (options = [], value = "") =>
  options.find((option) => String(option?.value) === String(value)) || null;

const findOptionLabelById = (options = [], id) => {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return "";

  const matchedOption = (Array.isArray(options) ? options : []).find((option) => {
    const optionId = Number(option?.optionId ?? option?.id ?? option?.value);
    return Number.isFinite(optionId) && optionId === normalizedId;
  });

  return toText(matchedOption?.label || matchedOption?.name || matchedOption?.title);
};

const resolveRelationLabel = (
  relation,
  fallbackId,
  prefix = "Item",
  options = []
) => {
  const directLabel = pickFirstText(
    relation?.name,
    relation?.title,
    relation?.label,
    relation?.branch_name,
    relation?.equipment_type_name,
    relation?.vendor_name,
    relation?.registration_number
  );

  if (directLabel) return directLabel;

  const dropdownLabel = findOptionLabelById(options, fallbackId);
  if (dropdownLabel) return dropdownLabel;

  const id = toComparableNumber(fallbackId);
  return id && id > 0 ? `${prefix} #${id}` : "";
};

const resolveEquipmentVehicleLabel = (row) => {
  const directLabel = pickFirstText(
    row?.vehicle?.vehicle_id,
    row?.vehicle?.registration_number,
    row?.vehicle?.name
  );

  if (directLabel) return directLabel;

  const vehicleId = toComparableNumber(row?.vehicle_id);
  return vehicleId && vehicleId > 0 ? `VE-${vehicleId}` : "N/A";
};

const normalizeTyreAssignmentValue = (value) => {
  const normalized = toText(value);
  if (!normalized) return "";

  const lower = normalized.toLowerCase();
  if (lower === "0" || lower === "null" || lower === "n/a") {
    return "";
  }

  return normalized;
};

const resolveEquipmentTyreLabels = (row) => {
  const tyreSource = Array.isArray(row?.current_tyres)
    ? row.current_tyres
    : Array.isArray(row?.asset_axles)
      ? row.asset_axles
      : [];

  if (!tyreSource.length) return [];

  return tyreSource
    .flatMap((axle) => {
      const directTyreLabel = toText(
        axle?.tyres_id ||
          axle?.systemId ||
          axle?.tyre_id ||
          axle?.serial_number
      );

      if (directTyreLabel) {
        return [directTyreLabel];
      }

      return [
        axle?.outer_left_tyre_id,
        axle?.inner_left_tyre_id,
        axle?.inner_right_tyre_id,
        axle?.outer_right_tyre_id,
        axle?.left_tyre_id,
        axle?.right_tyre_id,
        axle?.left_inner_tyre_id,
        axle?.left_outer_tyre_id,
        axle?.right_inner_tyre_id,
        axle?.right_outer_tyre_id,
      ]
        .map((tyreId) => {
          const normalizedTyreId = toComparableNumber(tyreId);
          return normalizedTyreId ? `TY-${normalizedTyreId}` : "";
        })
        .filter(Boolean);
    })
    .filter(Boolean)
    .filter((label, index, items) => items.indexOf(label) === index);
};

const resolveEquipmentTypeLabel = (
  row,
  equipmentTypeDropdownOptions = [],
  fallbackEquipment = null
) => {
  const directLabel = pickFirstText(
    row?.equipment_type?.equipment_type_name,
    row?.equipment_type?.name,
    row?.equipment_type_name,
    row?.equipmentType?.name,
    row?.equipmentType,
    row?.type?.name,
    fallbackEquipment?.formData?.equipmentType,
    fallbackEquipment?.equipmentType
  );

  if (directLabel) return directLabel;

  const matchedOption = findOptionByValue(
    equipmentTypeDropdownOptions,
    row?.equipment_type_id
  );

  if (matchedOption?.label) return matchedOption.label;

  const equipmentTypeId = toComparableNumber(row?.equipment_type_id);
  return equipmentTypeId && equipmentTypeId > 0 ? `Type #${equipmentTypeId}` : "--";
};

const formatDisplayLabel = (value) => {
  const normalized = toText(value);
  if (!normalized) return "";

  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeStatusValue = (value = "") =>
  toText(value)
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const buildInstallmentSchedule = (assetInstallments = [], fallbackPaymentMethod = "") => {
  if (!Array.isArray(assetInstallments) || !assetInstallments.length) {
    return [];
  }

  return assetInstallments.map((item, index) => ({
    number: String(index + 1).padStart(2, "0"),
    dueDate: formatDateOnly(item?.first_payment_date),
    amount: toComparableNumber(item?.installment_amount) || 0,
    paymentMethod: pickFirstText(
      item?.payment_method,
      item?.paymentMethod,
      fallbackPaymentMethod
    ),
    attachmentsCount: Array.isArray(item?.files) ? item.files.length : 0,
    status: toText(item?.status).toLowerCase() || "pending",
  }));
};

const buildVehicleHistory = (history = []) => {
  if (!Array.isArray(history) || !history.length) return [];

  return history.map((item) => ({
    equipmentId: pickFirstText(
      item?.equipment_id,
      item?.equipment?.equipment_id,
      item?.vehicle_id,
      item?.vehicle?.vehicle_id,
      item?.vehicle?.registration_number
    ),
    startDate: formatDateOnly(item?.start_date || item?.assigned_at || item?.created_at),
    endDate: formatDateOnly(item?.end_date || item?.unassigned_at || item?.updated_at),
    duration: pickFirstText(item?.duration, item?.assignment_duration, item?.info),
  }));
};

const buildTyreHistory = (history = []) => {
  if (!Array.isArray(history) || !history.length) return [];

  return history.map((item) => ({
    tyreId: pickFirstText(
      item?.tyre_id,
      item?.asset_tyre_id,
      item?.tyre?.tyre_id,
      item?.tyre?.tyreId
    ),
    position: pickFirstText(
      item?.position,
      item?.installed_position,
      item?.position_name,
      item?.mounted_position,
      item?.mount_position,
      item?.current_position,
      item?.info
    ),
    installedDate: formatDateOnly(
      item?.installed_at || item?.installed_date || item?.start_date || item?.created_at
    ),
    removedDate: formatDateOnly(
      item?.removed_at || item?.removed_date || item?.end_date
    ),
    startOdometer:
      formatDisplayNumber(
        item?.start_odometer ??
          item?.installed_odometer ??
          item?.start_odm ??
          item?.installed_odm ??
          item?.start_odo_meter ??
          item?.odometer_at_installation
      ) || "-",
    endOdometer:
      formatDisplayNumber(
        item?.end_odometer ??
          item?.removed_odometer ??
          item?.end_odm ??
          item?.removed_odm ??
          item?.end_odo_meter ??
          item?.odometer_at_removal
      ) || "-",
    kmDriven: formatDisplayNumber(item?.km_driven ?? item?.distance_driven ?? item?.distance) || "-",
  }));
};

const buildAxleConfigurations = (assetAxles = []) => {
  if (!Array.isArray(assetAxles) || !assetAxles.length) return [];

  return assetAxles.map((item, index) => {
    const hasDualSetup = [
      item?.inner_left_tyre_id,
      item?.inner_right_tyre_id,
      item?.left_inner_tyre_id,
      item?.right_inner_tyre_id,
    ].some((value) => normalizeTyreAssignmentValue(value));

    return {
      id: item?.id || "",
      sourceId: item?.source_id || "",
      sourceType: item?.source_type || "",
      axleId: pickFirstText(item?.name, item?.asset_axle_id) || `A${index + 1}`,
      tyreConfiguration: hasDualSetup ? "dual" : "single",
      assignments: {
        left: normalizeTyreAssignmentValue(item?.left_tyre_id || item?.outer_left_tyre_id),
        right: normalizeTyreAssignmentValue(item?.right_tyre_id || item?.outer_right_tyre_id),
        leftInner: normalizeTyreAssignmentValue(
          item?.inner_left_tyre_id || item?.left_inner_tyre_id
        ),
        leftOuter: normalizeTyreAssignmentValue(
          item?.outer_left_tyre_id || item?.left_outer_tyre_id
        ),
        rightInner: normalizeTyreAssignmentValue(
          item?.inner_right_tyre_id || item?.right_inner_tyre_id
        ),
        rightOuter: normalizeTyreAssignmentValue(
          item?.outer_right_tyre_id || item?.right_outer_tyre_id
        ),
      },
    };
  });
};

export const mapEquipmentServiceRows = (services = []) =>
  (Array.isArray(services) ? services : []).map((service) => ({
    id: pickFirstText(service?.service_id, service?.id) || "-",
    workOrderId:
      pickFirstText(
        service?.work_order?.work_order_id,
        service?.work_order?.id,
        service?.work_order_id,
        service?.workOrderId
      ) || "-",
    serviceType:
      pickFirstText(service?.services_type, service?.service_type, service?.serviceType)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()) || "-",
    serviceDate:
      formatDateOnly(service?.start_at || service?.complete_at || service?.service_date) || "-",
    odometer: formatDisplayNumber(service?.odo_meter) || "-",
    partCost: toComparableNumber(service?.cost_parts) || 0,
    labourCost: toComparableNumber(service?.cost_labor) || 0,
    totalCost:
      toComparableNumber(service?.complete_total ?? service?.sub_total ?? service?.total_cost) || 0,
    status: normalizeStatusValue(service?.status || "completed") || "completed",
  }));

export const mapEquipmentInstallmentRows = (installments = []) =>
  (Array.isArray(installments) ? installments : []).map((item, index) => ({
    number: String(item?.order_number ?? index + 1).padStart(2, "0"),
    paymentMethod:
      formatDisplayLabel(pickFirstText(item?.payment_method, item?.paymentMethod)) ||
      "Bank Transfer",
    amount: toComparableNumber(item?.amount || item?.installment_amount) || 0,
    submissionDate: formatDateOnly(item?.submit_at || item?.submission_date || item?.dueDate),
    dueDate: formatDateOnly(item?.submit_at || item?.submission_date || item?.dueDate),
    attachmentsCount: Array.isArray(item?.installment_files)
      ? item.installment_files.length
      : Array.isArray(item?.files)
        ? item.files.length
        : 0,
    attachments: Array.isArray(item?.installment_files)
      ? item.installment_files
      : Array.isArray(item?.files)
        ? item.files
        : [],
    status: normalizeStatusValue(item?.status || "pending") || "pending",
  }));

export const mapEquipmentTripRows = (trips = []) =>
  (Array.isArray(trips) ? trips : []).map((trip) => ({
    id: pickFirstText(trip?.trip_id, trip?.id) || "-",
    driverName:
      pickFirstText(
        trip?.primary_driver?.username,
        trip?.primary_driver?.name,
        trip?.driver?.username,
        trip?.driver?.name
      ) || "-",
    driverImg:
      trip?.primary_driver?.image ||
      trip?.driver?.image ||
      "",
    startDate: formatDateOnly(trip?.start_at || trip?.actual_start_at) || "-",
    endDate: formatDateOnly(trip?.actual_end_at || trip?.expected_end_at) || "-",
    distanceValue: toComparableNumber(trip?.distance ?? trip?.covered_distance) || 0,
    distance: formatDisplayNumber(trip?.distance ?? trip?.covered_distance) || "-",
  }));

export const mapEquipmentVehicleHistoryRows = (
  history = [],
  { vehicleOptions = [] } = {}
) => {
  if (!Array.isArray(history) || !history.length) return [];

  return history.map((item) => {
    const startDate = formatDateOnly(
      item?.start_date || item?.assigned_at || item?.installed_at || item?.created_at
    );
    const endDate = formatDateOnly(item?.end_date || item?.unassigned_at || item?.removed_at);

    return {
      equipmentId: pickFirstText(
        item?.vehicle?.registration_number,
        item?.vehicle?.vehicle_id,
        findOptionLabelById(vehicleOptions, item?.vehicle_id),
        item?.vehicle_id,
        item?.equipment_id,
        item?.equipment?.equipment_id
      ) || "-",
      startDate: startDate || "-",
      endDate: endDate || "-",
      duration:
        pickFirstText(item?.duration, item?.duration_text, item?.assignment_duration, item?.info) ||
        formatDurationBetween(
          item?.installed_at || item?.assigned_at,
          item?.removed_at || item?.unassigned_at,
          {
            current: Boolean(item?.current) && !item?.removed_at && !item?.unassigned_at,
          }
        ),
    };
  });
};

export const mapEquipmentTyreHistoryRows = (
  history = [],
  { tyreOptions = [] } = {}
) => {
  if (!Array.isArray(history) || !history.length) return [];

  return history.map((item) => ({
    tyreId: pickFirstText(
      item?.tyre?.tyres_id,
      item?.tyre?.system_id,
      item?.tyres_id,
      findOptionLabelById(tyreOptions, item?.tyre?.id),
      findOptionLabelById(tyreOptions, item?.tyre_id),
      item?.tyre_id,
      item?.asset_tyre_id,
      item?.tyre?.tyre_id
    ) || "-",
    position:
      pickFirstText(
        item?.position,
        item?.installed_position,
        item?.position_name,
        item?.mounted_position,
        item?.mount_position,
        item?.current_position,
        item?.info
      ) || "-",
    installedDate: formatDateOnly(
      item?.installed_date || item?.installed_at || item?.start_date || item?.created_at
    ) || "-",
    removedDate: formatDateOnly(
      item?.removed_date || item?.removed_at || item?.end_date
    ) || "-",
    startOdometer:
      formatDisplayNumber(
        item?.start_odometer ??
          item?.installed_odometer ??
          item?.start_odm ??
          item?.installed_odm ??
          item?.start_odo_meter ??
          item?.odometer_at_installation
      ) || "-",
    endOdometer:
      formatDisplayNumber(
        item?.end_odometer ??
          item?.removed_odometer ??
          item?.end_odm ??
          item?.removed_odm ??
          item?.end_odo_meter ??
          item?.odometer_at_removal
      ) || "-",
    kmDriven: formatDisplayNumber(item?.km_driven ?? item?.distance_driven ?? item?.distance) || "-",
  }));
};

export const mapEquipmentAxleConfigurations = (assetAxles = []) =>
  buildAxleConfigurations(assetAxles);

export const mapEquipmentApiRow = (
  row,
  equipmentTypeDropdownOptions = [],
  fallbackEquipment = null,
  {
    manufacturerOptions = [],
    modelOptions = [],
    vendorOptions = [],
  } = {}
) => {
  const purchase = Array.isArray(row?.asset_purchases)
    ? row.asset_purchases[0] || {}
    : row?.asset_purchases || row?.asset_purchase || {};
  const installmentList = Array.isArray(row?.asset_installments)
    ? row.asset_installments
    : row?.asset_installments
      ? [row.asset_installments]
      : [];
  const installment = installmentList[0] || {};
  const insurance = Array.isArray(row?.asset_insurances)
    ? row.asset_insurances[0] || {}
    : row?.asset_insurances || {};
  const systemId = toText(row?.equipment_id || row?.systemId || row?.system_id || row?.id);
  const equipmentType = resolveEquipmentTypeLabel(
    row,
    equipmentTypeDropdownOptions,
    fallbackEquipment
  );
  const branchLabel = resolveRelationLabel(row?.branch, row?.branch_id, "Branch");
  const linkedVehicleId = resolveEquipmentVehicleLabel(row);
  const vehicleId = toComparableNumber(row?.vehicle?.id ?? row?.vehicle_id) || "";
  const usageMeterType = pickFirstText(row?.usage_meter_type, row?.usageMeterType);
  const usageMeterReadingValue =
    toComparableNumber(
      row?.usage_meter_reading ??
        row?.usageMeterReading ??
        row?.meter_reading ??
        row?.odm ??
        row?.vehicle?.odm
    ) || 0;
  const capacityValue = toComparableNumber(row?.capacity) || 0;
  const capacityUnit = pickFirstText(row?.capacity_unit, row?.capacityUnit);
  const purchaseCost =
    toComparableNumber(
      purchase?.purchase_cost ??
        purchase?.purchaseCost ??
        row?.purchase_cost ??
        row?.purchaseCost
    ) || 0;
  const purchaseCostDisplay = toText(
    purchase?.purchase_cost ??
      purchase?.purchaseCost ??
      row?.purchase_cost ??
      row?.purchaseCost
  );
  const purchaseCostValue = numberOrBlank(
    purchase?.purchase_cost ??
      purchase?.purchaseCost ??
      row?.purchase_cost ??
      row?.purchaseCost
  );
  const premiumAmountValue = numberOrBlank(
    insurance?.premium_amount ??
      insurance?.premiumAmount ??
      insurance?.insurance_cost ??
      insurance?.insuranceCost ??
      row?.premium_amount ??
      row?.premiumAmount ??
      row?.insurance_cost ??
      row?.insuranceCost
  );
  const maintenanceCostValue = numberOrBlank(
    row?.maintenance_cost ?? row?.maintenanceCost
  );
  const tyreCostValue = numberOrBlank(row?.tyre_cost ?? row?.tyreCost);
  const avgCostPerTyreValue = numberOrBlank(
    row?.avg_cost_per_tyre ??
      row?.avgCostPerTyre ??
      row?.average_cost_per_tyre ??
      row?.averageCostPerTyre
  );
  const numberOfTyres = toComparableNumber(row?.number_of_tyres) || 0;
  const manufacturerLabel = resolveRelationLabel(
    row?.manufacturer,
    row?.manufacturer_id,
    "Manufacturer",
    manufacturerOptions
  );
  const modelLabel = resolveRelationLabel(
    row?.model,
    row?.model_id,
    "Model",
    modelOptions
  );
  const supplierVendorLabel = resolveRelationLabel(
    row?.supplier_vendor,
    row?.supplier_vendor_id,
    "Vendor",
    vendorOptions
  );
  const purchaseVendorLabel = resolveRelationLabel(
    purchase?.vendor,
    purchase?.vendor_id,
    "Vendor",
    vendorOptions
  );
  const linkedTyreLabels = resolveEquipmentTyreLabels(row);

  return {
    ...row,
    id: row?.id,
    systemId,
    equipmentId: systemId,
    registrationNumber: pickFirstText(row?.registration_number, row?.vehicle?.registration_number),
    equipmentType,
    equipmentTypeId: row?.equipment_type_id || "",
    serialNumber: toText(row?.serial_number),
    capacityValue,
    capacityUnit,
    capacity: `${capacityValue.toLocaleString("en-US")} ${capacityUnit}`.trim(),
    branchId: row?.branch_id || "",
    branch: branchLabel || "--",
    linkedVehicleRecord: row?.vehicle || null,
    linkedVehicleId,
    vehicleId,
    linkedTyres: linkedTyreLabels.length || numberOfTyres,
    linkedTyreLabels,
    usageMeterType,
    usageMeterReadingValue,
    usageMeterReading: formatUsageMeterReading(usageMeterReadingValue, usageMeterType),
    nextServiceDue: formatDateOnly(insurance?.expiry_date),
    status: normalizeStatusValue(row?.status) || "inactive",
    totalCost: purchaseCost,
    totalCostDisplay: purchaseCostDisplay,
    description: row?.description || "",
    notes: row?.notes || fallbackEquipment?.notes || "",
    formData: {
      vehicleId,
      linkedVehicleId,
      registrationNumber: toText(row?.registration_number),
      equipmentTypeId: row?.equipment_type_id || "",
      equipmentType,
      serialNumber: toText(row?.serial_number),
      capacityValue: numberOrBlank(row?.capacity),
      capacityUnit,
      usageMeterType,
      usageMeterReadingValue: numberOrBlank(
        row?.usage_meter_reading ??
          row?.usageMeterReading ??
          row?.meter_reading ??
          row?.odm ??
          row?.vehicle?.odm
      ),
      branchId: row?.branch_id || "",
      branch: branchLabel,
      status: normalizeStatusValue(row?.status) || "inactive",
      description: row?.description || "",
      manufacturerId: row?.manufacturer_id || "",
      manufacturer: manufacturerLabel,
      modelId: row?.model_id || "",
      model: modelLabel,
      yearOfManufacture: row?.year_of_manufacture || "",
      countryOfOrigin: toText(row?.country_of_origin ?? row?.countryOfOrigin),
      supplierVendorId: row?.supplier_vendor_id || "",
      supplierVendor: supplierVendorLabel,
      assetNumber: toText(row?.asset_number),
      numberOfAxles: numberOrBlank(row?.number_of_axles),
      brakeSystem: toText(row?.brake_system),
      numberOfTyres: numberOrBlank(row?.number_of_tyres),
      suspensionType: toText(row?.suspension_type),
      tankerBowserCapacity: numberOrBlank(row?.tanker_bowser_capacity),
      numberOfCompartments: numberOrBlank(row?.number_of_compartments),
      loadCapacity: numberOrBlank(row?.load_capacity),
      containerSize: toText(row?.container_size),
      length: numberOrBlank(row?.length),
      width: numberOrBlank(row?.width),
      height: numberOrBlank(row?.height),
      tareWeight: numberOrBlank(row?.tare_weight),
      grossWeight: numberOrBlank(row?.gross_weight),
      purchaseDate: formatDateOnly(
        purchase?.purchase_at ??
          purchase?.purchaseDate ??
          row?.purchase_at ??
          row?.purchase_date ??
          row?.purchaseDate
      ),
      purchaseCost: purchaseCostValue,
      paymentType: toText(
        purchase?.payment_type ??
          purchase?.paymentType ??
          row?.payment_type ??
          row?.paymentType
      ),
      paymentMethod: toText(
        purchase?.payment_method ??
          purchase?.paymentMethod ??
          row?.payment_method ??
          row?.paymentMethod
      ),
      vendorId: purchase?.vendor_id || "",
      vendor: purchaseVendorLabel,
      depreciationMethod: toText(
        purchase?.depreciation_method ??
          purchase?.depreciationMethod ??
          row?.depreciation_method ??
          row?.depreciationMethod
      ),
      usefulLife: numberOrBlank(
        purchase?.useful_life ??
          purchase?.usefulLife ??
          row?.useful_life ??
          row?.usefulLife
      ),
      residualValue: numberOrBlank(
        purchase?.residual_value ??
          purchase?.residualValue ??
          row?.residual_value ??
          row?.residualValue
      ),
      totalInstallments: numberOrBlank(
        installment?.total_installments ??
          installment?.totalInstallments ??
          row?.total_installments ??
          row?.totalInstallments
      ),
      installmentAmount: numberOrBlank(
        installment?.installment_amount ??
          installment?.installmentAmount ??
          row?.installment_amount ??
          row?.installmentAmount
      ),
      installmentFrequency: toText(
        installment?.installment_frequency ??
          installment?.installmentFrequency ??
          row?.installment_frequency ??
          row?.installmentFrequency
      ),
      firstPaymentDate: formatDateOnly(
        installment?.first_payment_date ??
          installment?.firstPaymentDate ??
          row?.first_payment_date ??
          row?.firstPaymentDate
      ),
      interestRate: numberOrBlank(
        installment?.interest_rate ??
          installment?.interestRate ??
          row?.interest_rate ??
          row?.interestRate
      ),
      financingInstitution: toText(
        installment?.financing_institution ??
          installment?.financingInstitution ??
          row?.financing_institution ??
          row?.financingInstitution
      ),
      insuranceProvider: toText(insurance?.provider),
      policyNumber: toText(insurance?.policy_number),
      coverageType: toText(insurance?.coverage_type),
      coverageAmount: numberOrBlank(
        insurance?.coverage_amount ??
          insurance?.coverageAmount ??
          row?.coverage_amount ??
          row?.coverageAmount
      ),
      premiumAmount: premiumAmountValue,
      insuranceStartDate: formatDateOnly(
        insurance?.start_date ??
          insurance?.startDate ??
          row?.insurance_start_date ??
          row?.insuranceStartDate
      ),
      insuranceExpiryDate: formatDateOnly(
        insurance?.expiry_date ??
          insurance?.expiryDate ??
          row?.insurance_expiry_date ??
          row?.insuranceExpiryDate
      ),
      insuranceStatus: toText(
        insurance?.status ??
          insurance?.insurance_status ??
          insurance?.insuranceStatus ??
          row?.insurance_status ??
          row?.insuranceStatus
      ),
      insuranceCost: premiumAmountValue,
      maintenanceCost: maintenanceCostValue,
      tyreCost: tyreCostValue,
      avgCostPerTyre: avgCostPerTyreValue,
      attachments: Array.isArray(row?.files) ? row.files : [],
      installmentSchedule: buildInstallmentSchedule(
        installmentList,
        pickFirstText(purchase?.payment_method, purchase?.paymentMethod)
      ),
      vehicleHistory: buildVehicleHistory(row?.equipment_installed_history),
      axleConfigurations: buildAxleConfigurations(row?.asset_axles),
      purchaseRecord: purchase,
      installmentRecord: installment,
      insuranceRecord: insurance,
      equipmentInstalledHistory: Array.isArray(row?.equipment_installed_history)
        ? row.equipment_installed_history
        : [],
      axleHistory: Array.isArray(row?.asset_axles) ? row.asset_axles : [],
      tyreHistory: buildTyreHistory(row?.tyre_installed_history),
    },
  };
};
